import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export type Language = "en" | "zh";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

// Translation data
const translations = {
  en: {
    // Navigation
    "nav.experience": "Experience",
    "nav.portfolio": "Portfolio",
    "nav.skills": "Skills",
    "nav.contact": "Contact",
    "nav.close": "Close",
    "nav.skipToMain": "Skip to main content",
    "project.nav.back": "All Projects",
    "project.nav.prev": "Previous",
    "project.nav.next": "Next",
    "aria.projectNav": "Project navigation",

    // Home section
    "home.greeting": "Hi, I'm",
    "home.name": "Yuanyuan Li",
    "home.title1": "VP, Software Engineering Manager",
    "home.title2": "Full Stack + Data Platform Engineering",
    "home.title3": "10 Years Engineering Leadership Experience",
    "home.downloadResume": "Download Resume",
    "home.loading": "Loading...",

    // ARIA labels
    "aria.portfolioWebsite": "Portfolio website",
    "aria.portfolioHomepage": "Portfolio homepage",
    "aria.nameTitle": "Yuanyuan Li, VP Software Engineering Manager",
    "aria.downloadResume": "Download Yuanyuan Li's resume PDF",
    "aria.socialLinks": "Social media links",
    "aria.linkedinProfile": "Visit Yuanyuan Li's LinkedIn profile",
    "aria.githubProfile": "Visit Yuanyuan Li's GitHub profile",
    "aria.facebookProfile": "Visit Yuanyuan Li's Facebook profile",
    "aria.mainNavigation": "Main navigation menu",
    "aria.secondaryNavigation": "Secondary navigation menu",
    "aria.portfolioSections": "Portfolio sections",
    "aria.viewExperience": "View experience section",
    "aria.viewPortfolio": "View portfolio section",
    "aria.viewSkills": "View skills section",
    "aria.viewContact": "View contact section",
    "aria.navigateToExperience": "Navigate to experience section",
    "aria.navigateToPortfolio": "Navigate to portfolio section",
    "aria.navigateToSkills": "Navigate to skills section",
    "aria.navigateToContact": "Navigate to contact section",
    "aria.closeNavigation": "Close navigation and return to home",
    "aria.experienceContent": "Experience content",
    "aria.portfolioContent": "Portfolio content",
    "aria.skillsContent": "Skills content",
    "aria.contactContent": "Contact content",

    // Language switcher
    "lang.switchTo": "Switch to",
    "lang.english": "English",
    "lang.chinese": "中文",

    // Experience page
    "exp.profile": "Profile",
    "exp.experience": "Experience",
    "exp.education": "Education",
    "exp.awards": "Awards",
    "exp.name": "Name",
    "exp.location": "Location",
    "exp.email": "Email",
    "exp.skype": "LinkedIn",
    "exp.interest": "Interest",
    "exp.current": "Current",
    "exp.contactMessage":
      "Feel free to contact me for any interesting projects and jobs",

    // Skills page
    "skills.title": "Skills",
    "skills.technical": "Full-Stack & AI",
    "skills.cloud": "Cloud & Data Infrastructure",
    "skills.knowhow": "Engineering Leadership",

    // Contact page
    "contact.getInTouch": "Get in touch",
    "contact.location": "Location",
    "contact.email": "Email",
    "contact.contactMessage":
      "Feel free to contact me for any interesting projects and jobs",

    // Experience page detailed content
    "exp.profileDesc1":
      "VP of Software Engineering with 10 years of experience, where I built and led a full-stack and data engineering team from the ground up at Two Sigma (Venn). I partner closely with Product, Design, Sales, and Business stakeholders to deliver high-impact data products, from client-facing API platforms and UI features to end-to-end ETL pipelines on AWS. I have driven four internal promotions and maintain a strong focus on engineering excellence, data quality, and team growth.",
    "exp.profileDesc2":
      "Cornell MS in Information Science (HCI focus) with a background spanning Management, Economics, and Computer Science. I have taken projects from initial whiteboard to production across mobile, full-stack web, and distributed data systems, and I build and lead teams that do the same. Equally comfortable writing TypeScript and Python, reviewing architecture, and presenting quarterly roadmaps to senior leadership.",
    "exp.personalInfo.name": "Yuanyuan (Maggie) Li",
    "exp.personalInfo.location": "New York City Metropolitan Area",
    "exp.personalInfo.email": "yuanyuanli2022@gmail.com",
    "exp.personalInfo.skype": "linkedin.com/in/yuanyuanmaggie",
    "exp.personalInfo.interest":
      "VP Engineering, Software Engineering Manager, Data Platform",

    // Portfolio page
    "portfolio.title": "Portfolio",
    "portfolio.subtitle": "Recent Works",
    "portfolio.viewProject": "View Project",
    "portfolio.seeDemo": "See Demo",
    "portfolio.sourceCode": "Source Code",
    "portfolio.backToPortfolio": "Back to Portfolio",
    "portfolio.interestedInMe": "Interested in me?",

    // Portfolio filters
    "portfolio.filter.all": "All",
    "portfolio.filter.work": "Work Experience",
    "portfolio.filter.side": "Side Projects",
    "portfolio.filter.school": "Research & Academic",

    // Project titles and descriptions
    "project.venn.title": "Venn (Two Sigma)",
    "project.venn.desc": "Software Engineering Manager",
    "project.aiassistant.title": "AI Profile Assistant",
    "project.aiassistant.desc":
      "Serverless conversational + voice chatbot (CAG)",
    "project.dag.title": "DAG Workflow Engine",
    "project.dag.desc": "Visual workflow builder + concurrent DAG executor",
    "project.jet.title": "Jet.com (Walmart)",
    "project.jet.desc": "Front End Engineer",
    "project.supply.title": "SupplyHouse.com",
    "project.supply.desc": "Web development",
    "project.yumso.title": "Yumso Inc",
    "project.yumso.desc": "Web development",
    "project.d3.title": "Data-Driven Web Application (D3.js)",
    "project.d3.desc": "Data Visualization",
    "project.hci.title": "Human-Computer Interaction Design Project",
    "project.hci.desc": "UI/UX design",
    "project.se.title": "Software Engineering Clients Project",
    "project.se.desc": "Web development",
    "project.fitcat.title": "Cornell Feline Health Center Project",
    "project.fitcat.desc": "iOS development",
    "project.givenext.title": "Donation Startup Business Solution",
    "project.givenext.desc": "UI/UX design",
    "project.cscw.title": "Computer Supported Collaboration Research",
    "project.cscw.desc": "UX research",
    "project.dataproduct.title": "Designing Data Product MBAs Project",
    "project.dataproduct.desc": "Data analysis and visualization",
    "project.nonprofit.title": "Non-profit Business Solution (Hackathon)",
    "project.nonprofit.desc": "Web development",
    "project.rapid.title": "Rapid Prototyping and Physical Computing",
    "project.rapid.desc": "Hardware and prototyping",

    // Experience details - Two Sigma
    "exp.twosigma.bullet1":
      "Built a high-performing Data Engineering team from the ground up, growing from 0 to 5 engineers while establishing a strong team culture centered on ownership, collaboration, and impact.",
    "exp.twosigma.bullet2":
      "Led the team to deliver a comprehensive data platform that supports the entire data lifecycle, including data ingestion, transformation, storage, and serving, enabling the organization to leverage data for strategic decision-making.",
    "exp.twosigma.bullet3":
      "Spearheaded the development of a data product that provides a unified view of data across the organization, enhancing data accessibility and usability for internal stakeholders.",
    "exp.twosigma.bullet4":
      "Championed the adoption of best practices in data engineering, including data quality, governance, and security, ensuring that data is reliable, secure, and compliant with industry standards.",
    "exp.twosigma.bullet5":
      "Collaborated with cross-functional teams, including product managers, data scientists, and business stakeholders, to align data engineering efforts with business objectives and deliver high-impact data solutions.",
    "exp.twosigma.bullet6":
      "Drove the implementation of Agile methodologies within the team, resulting in improved project delivery timelines and enhanced team productivity.",
    "exp.twosigma.bullet7":
      "Mentored and coached team members, providing guidance on technical challenges and career development, fostering a culture of learning and growth within the team.",
    "exp.twosigma.bullet8":
      "Implemented robust data pipelines using modern technologies such as AWS services, ensuring efficient data processing and real-time analytics capabilities.",
    "exp.twosigma.bullet9":
      "Recognized for exceptional leadership and impact, receiving accolades from senior leadership for contributions to the organization's data strategy and execution.",

    // Experience details - Jet.com
    "exp.jet.bullet1":
      "Leader of a five-person group charged with rebuilding order history flow with React & Redux. Responsible for planning, scoping and improving team's workflow each Agile sprint.",
    "exp.jet.bullet2":
      "Architected flexible analytics library to help a team of business analysts track customer behavior.",
    "exp.jet.bullet3":
      "Maintained features include customer checkout, search suggestions, and promotions. Work with the full toolkit of the front-end stack including pug (jade), Stylus and express.js (for development) and Cucumber, Mocha and WebdriverIO (for testing).",
    "exp.jet.bullet4":
      "Collaborated with product designers, product managers, marketing analytics, and API engineers to deliver features.",
    "exp.jet.bullet5":
      "Led a cross-functional team that won the top prize at Jet's internal hackathon (Jet Innovation Time). The idea has been developed into a new product line at Jet.",
    "exp.jet.bullet6":
      "Independently developed front-end load testing suite in C# for load-testing peak holiday demand. Worked with business analysts, DevOps and other upstream teams to scale the order system to be able to handle 3x the expected business goal.",
    "exp.jet.bullet7":
      "Experienced in front-end accessibility, cross-browser compatibility, search engine optimization (SEO), and web performance optimization.",
    "exp.jet.bullet8":
      "Served on pager duty for the team troubleshooting real-time production issues. The system supports hundreds of thousands of monthly transactions.",
    "exp.jet.bullet9": "Jet meetup presenter",

    // Experience details - SupplyHouse
    "exp.supply.bullet1":
      "Developed fully functional and responsive websites for e-commerce built with Java (along with FreeMarker), JavaScript, HTML5, CSS3, jQuery, and Bootstrap.",
    "exp.supply.bullet2":
      "Created code & design guidelines shaping feedback from graphic designers, marketers and software engineers.",
    "exp.supply.bullet3":
      "Designed prototypes based on business requirements and then translated prototypes into clean launch-ready pages with reusable user interface (UI) components.",
    "exp.supply.bullet4":
      "Tested front-end code in multiple browsers to ensure cross-browser compatibility.",
    "exp.supply.bullet5":
      "Managed set of websites using PHP, MySQL, and WordPress.",

    // Experience details - Yumso
    "exp.yumso.bullet1":
      "Maintained, developed and designed our startup platform enabling users to connect with local chefs with AngularJS, D3.js, and Sass.",
    "exp.yumso.bullet2":
      "Executed and contributed to full-stack web development with an emphasis on front-end features and cross-browser compatibility.",
    "exp.yumso.bullet3":
      "Collaborated with a virtual and cross-functional team using agile development methodology.",

    // Experience details - PwC
    "exp.pwc.bullet1":
      "Analyzed migration requirements for a legacy system hand-in-hand with the client.",
    "exp.pwc.bullet2":
      "Developed and tested IT system specifications and maintained project documentation.",

    // Experience details - SAP
    "exp.sap.bullet1":
      "Analyzed clients' requirements and improved customers' satisfaction by redesigning processes.",
    "exp.sap.bullet2":
      "Cooperated with global IT architects to finish 20+ reports on SAP ERP integration, HANA in-memory migration, and System Landscape Optimization (SLO).",

    // Education details
    "exp.cornell.degree":
      "Master of Professional Studies in Information Science",
    "exp.cornell.gpa": "GPA: 3.73/4.0",
    "exp.cornell.courses":
      "Main courses: Database Systems, Software Engineering, Visual Data Analytics for Web, Designing Data Products, OOP and Data Structures, Human-Computer Interaction Design",

    "exp.uibe.master.degree": "Master of Economics",
    "exp.uibe.master.gpa": "GPA: 3.71/4.0",
    "exp.uibe.master.courses":
      "Main courses: Advanced Economics, System Analysis and Design, Economics Data Analysis, Research Methods, Technology and Finance, and International Business",

    "exp.uibe.bachelor.degree": "Bachelor of Economics",
    "exp.uibe.bachelor.gpa": "GPA: 3.86/4.0, rank 1/108",
    "exp.uibe.bachelor.courses":
      "Main courses: Electronic Business, Finance Banking, Micro-Economics, Macro-Economics, Web Development, Statistics and Accounting",

    // Awards
    "exp.award.guanghua":
      "Guanghua Scholarship (top 5%), First-class Scholarship (top 5%)",
    "exp.award.beijing.graduate": "Beijing Outstanding Graduate",
    "exp.award.academic": "Scholarship for Academic Performance (4/8,000+)",
    "exp.award.leadership": "Leadership Scholarship (6/8,000+)",
    "exp.award.national": "National Scholarship (top 1%)",
    "exp.award.beijing.merit": "Beijing Merit Student (top 1%)",
    "exp.award.ebusiness":
      '1st Prize and also awarded as "The Best Entrepreneurship" in National E-Business Planning Competition',

    // ProjectVenn translations
    "venn.title": "Venn (by Two Sigma)",
    "venn.role": "VP, Software Engineering Manager",
    "venn.bullet1":
      "Built a high-performing Data Engineering team from the ground up, growing from 0 to 5 engineers while establishing a strong team culture centered on ownership, collaboration, and impact.",
    "venn.bullet2":
      "Partnered closely with Product and Quant teams to deliver revenue-generating data products, including API platforms (Factor, Integration, Analytics), client-facing UI features, and a scalable onboarding framework tailored for private clients.",
    "venn.bullet3":
      "Drove cross-functional alignment across Sales, Marketing, Legal, Client Solutions, Design, and Product teams to deliver new features, and served as the primary point of contact for external data vendors on integrations and incident resolution.",
    "venn.bullet4":
      "Designed and implemented team operations routines including Agile sprint cadences, goal setting (OKRs), roadmap planning, milestone tracking, and incident review practices.",
    "venn.bullet5":
      "Scaled the ETL platform on AWS (Glue, Step Functions, Lambda, S3), improving data processing reliability and SLA compliance",
    "venn.bullet6":
      "Provided technical leadership and mentorship to the team in building full-stack features across the React/TypeScript frontend and Java/Python backend, deployed on AWS with EKS. Oversaw design decisions, conducted code reviews, and ensured engineering best practices.",
    "venn.bullet7":
      "Drove hiring, onboarding, and career development processes, resulting in four internal promotions and high engagement in growth conversations.",
    "venn.myRole": "Data Platform Team Engineering Manager",
    "venn.skillsUtilized": "ETL Development, Projects Management",
    "venn.roleLabel": "My role:",
    "venn.skillsLabel": "Skills utilized:",
    "venn.apiTitle": "Venn API",
    "venn.factorTitle": "Venn Factor Analytics",
    "venn.seeMoreWebsite": "See More at Websites",
    "venn.seeDemo": "See Venn Demo",
    "venn.dateLabel": "Date:",
    "venn.dateRange": "July 2018 - Present",
    "venn.closeProject": "Close Project",

    // ProjectJet translations
    "jet.title": "Jet.com",
    "jet.role": "Front End Engineer",
    "jet.bullet1":
      "Leader of a five-person group charged with rebuilding order history flow with React & Redux. Responsible for planning, scoping and improving team's workflow each Agile sprint.",
    "jet.bullet2":
      "Architected flexible analytics library to help a team of business analysts track customer behavior.",
    "jet.bullet3":
      "Maintained features include customer checkout, search suggestions, and promotions. Work with the full toolkit of the front-end stack including pug (jade), Stylus and express.js (for development) and Cucumber, Mocha and WebdriverIO (for testing).",
    "jet.bullet4":
      "Collaborated with product designers, product managers, marketing analytics, and API engineers to deliver features.",
    "jet.bullet5":
      "Led a cross-functional team that won the top prize at Jet's internal hackathon (Jet Innovation Time). The idea has been developed into a new product line at Jet.",
    "jet.bullet6":
      "Independently developed front-end load testing suite in C# for load-testing peak holiday demand. Worked with business analysts, DevOps and other upstream teams to scale the order system to be able to handle 3x the expected business goal.",
    "jet.bullet7":
      "Experienced in front-end accessibility, cross-browser compatibility, search engine optimization (SEO), and web performance optimization.",
    "jet.bullet8":
      "Served on pager duty for the team troubleshooting real-time production issues. The system supports hundreds of thousands of monthly transactions.",
    "jet.bullet9": "Jet meetup presenter",
    "jet.myRole": "Front-end developer",
    "jet.skillsUtilized": "React/redux, MVC, FE operation",
    "jet.roleLabel": "My role:",
    "jet.skillsLabel": "Skills utilized:",
    "jet.guestCheckout": "Guest Checkout",
    "jet.meetupTitle": "Jet Meetup Presentation",
    "jet.meetupSubtitle": "Front End Testing @ Jet.com",
    "jet.hackathon1Title": "Jet Innovation Hackathon",
    "jet.hackathon1Subtitle": "Jetness",
    "jet.hackathon1Github": "Github:",
    "jet.hackathon1Demo": "Jetness Hackathon Demo",
    "jet.hackathon1Author": "from",
    "jet.hackathon1Platform": "on",
    "jet.hackathon2Title": "Jet Innovation Hackathon 2",
    "jet.hackathon2Subtitle": "Cooking with Jet",
    "jet.hackathon2Demo": "Hackathon Demo",
    "jet.seeMoreWebsite": "See More at Websites",
    "jet.seeSlide": "See Slide",
    "jet.seeDemoWebsite": "See Demo Websites",
    "jet.dateLabel": "Date:",
    "jet.dateRange": "Jan 2017 - July 2018",
    "jet.closeProject": "Close Project",

    // ProjectSupply translations
    "supply.title": "SupplyHouse.com",
    "supply.role": "Web (UI/UX) Developer",
    "supply.description1":
      "Developed fully functional responsive new websites for e-commerce, using HTML5, CSS3, Bootstrap, jQuery, Java, AJAX and FreeMarker as well as tools like Sublime Text, Eclipse, SVN and Adobe Photoshop.",
    "supply.description2":
      "In addition to the development, I also designed prototypes based on business requirements and then translated prototypes and designs into clean, valid, and launch-ready pages. So I can bridge the gap between UI design and technical implementation, taking an active role on both sides and defined how the application looks as well as how it works.",
    "supply.myRole": "Front-end developer",
    "supply.skillsUtilized": "web development",
    "supply.roleLabel": "My role:",
    "supply.skillsLabel": "Skills utilized:",
    "supply.projectTitle": "New websites project",
    "supply.seeNewWebsites": "See New Websites",
    "supply.dateLabel": "Date:",
    "supply.dateRange": "Aug 2016 - Jan 2017",
    "supply.closeProject": "Close Project",

    // ProjectYumso translations
    "yumso.title": "Yumso Inc",
    "yumso.role": "Startup Co-founder",
    "yumso.description":
      "Developed chefs/admins/drivers' websites with AngularJS, D3.js, Sass, Bootstrap, jQuery, etc. Our app and website have been launched and put into production. I also executed and contributed to full-stack web development projects, with an emphasis on front end features, browser manipulation, and cross-browser compatibility",
    "yumso.myRole": "Full Stack Developer, UX Designer",
    "yumso.skillsUtilized":
      "AngularJS, data visualization, UX Design, Rapid Prototypes, Remote Collaboration",
    "yumso.roleLabel": "My role:",
    "yumso.skillsLabel": "Skills utilized:",
    "yumso.aboutCompany": "About Company",
    "yumso.companyDesc1":
      "Yumso is a marketplace where you can explore and order authentic gourmets made by cooking aficionados in your community. Bringing you the taste of home, Yumso improves food safety through unique supervision system. Our efficient delivery services bring freshly cooked meals to your door with small costs. Always enjoy high-quality food in the way you want, at the time you want.",
    "yumso.companyDesc2":
      "You can also apply to be a caterer on Yumso to share your home-style cooking with other food lovers. Value your time in your own kitchen, and make money at the same time.",
    "yumso.ideasStart": "Ideas start from frames",
    "yumso.adminPanel": "Admin panel frame sketch",
    "yumso.driverWorkflow": "Driver workflow frame sketch",
    "yumso.translatedIdeas": "Then translated ideas to responsive web apps",
    "yumso.seeDemo": "See Demo",
    "yumso.dateLabel": "Date:",
    "yumso.dateRange": "May 2016 - Present",
    "yumso.closeProject": "Close Project",

    // ProjectD3 translations
    "d3.title": "Data-Driven Web Applications",
    "d3.role": "Class project",
    "d3.description":
      "Working with data in the context of modern web applications. These include data representation with relational and non-relational databases, data mining to find patterns and make predictions, and graphical presentation for visualization.",
    "d3.myRole": "Front-end developer, Data scientist",
    "d3.skillsUtilized": "data mining and visualization, d3.js, javascript",
    "d3.roleLabel": "My role:",
    "d3.skillsLabel": "Skills utilized:",
    "d3.project1Title":
      "Project 1: Analysis of Hospital Costs for Top Diagnoses in the United States from 2011 to 2013 (static maps)",
    "d3.project2Title":
      "Project 2: Cornell graduates' starting salaries and cost of living by state/region/college (interactive maps)",
    "d3.project3Title":
      "Project 3: What are the chances of having depression? (responsive maps)",
    "d3.project4Title": "Project 4: D3 + React show real time SF muni bus data",
    "d3.selectRoute": "Select route:",
    "d3.hoverDetail":
      "Hover detail info and show the info with the route's color",
    "d3.seeDemo": "See Demo",
    "d3.dateLabel": "Date:",
    "d3.teamMembersLabel": "Team members:",
    "d3.project1Date": "Feb 2016 - Mar 2016",
    "d3.project1Team": "YuanYuan Li, Richard Quan, Akshay Tata",
    "d3.project2Date": "Mar 2016 - Apr 2016",
    "d3.project2Team": "Anavi Nahar, Darcy Branchini and Yuanyuan Li",
    "d3.project3Date": "April 2016 - May 2016",
    "d3.project3Team": "Lan Zhang, Rocio Perez Cano and Yuanyuan Li",
    "d3.closeProject": "Close Project",

    // ProjectHci translations
    "hci.title": "Human-Computer Interaction Design",
    "hci.role": "Class project",
    "hci.description":
      "In the HCI class and project, I learned how hardware and software design influence the interaction between people and computers. We also finished the HCI Dietary Restrictions Assistive technology project successfully.",
    "hci.myRole": "UI/UX Designer",
    "hci.skillsUtilized":
      "user interviews and analysis, UI design (Adobe PhotoShop), low quality prototypes and high quality prototypes (Axure RP/ Sketch 3)",
    "hci.roleLabel": "My role:",
    "hci.skillsLabel": "Skills utilized:",
    "hci.problems": "Problems",
    "hci.problemsDesc":
      "When arriving on a college campus for the first time, new students are forced to rapidly adapt to their new social and work environment over the course of their study. This task is daunting in its own right, but is made so much more difficult when the student's diet is limited by a GI disease, severe allergy, or other dietary restriction. Students with these conditions may often put their health at risk when they don't have the time or energy to ensure they are maintaining their diet, and may also feel socially limited when they are unable to eat at the same places as their friends.",
    "hci.solutions": "Our solutions",
    "hci.solutionsDesc":
      "To help Cornell students with diet restrictions, especially new students, we developed Cornell MenU: a mobile application designed to provide streamlined and filtered menu information about on-campus meal halls based on the user's dietary restriction that will also serve to connect newer students with older students with similar conditions to create an information sharing network.",
    "hci.paperPrototypes": "Paper prototypes",
    "hci.paperPrototypesDesc":
      "Our product is a mobile application which obviously meets the first goal that users need; an App format which is user friendly and convenient. We first used paper prototypes to test concepts and got quick feedback from target students.",
    "hci.lowQualityPrototypes": "Low quality prototypes:",
    "hci.lowQualityDemo": "Low quality prototypes demo",
    "hci.highQualityPrototypes": "High Quality Prototypes",
    "hci.highQualityDesc":
      "After individual and group heuristic evaluations on the paper prototypes, we built our high quality prototype by Photoshop and Axure RP.",
    "hci.highQualityPrototypes2": "High quality prototypes",
    "hci.iterativePrototypes": "Iterative Prototypes",
    "hci.highQualityPrototypes3": "High quality prototypes:",
    "hci.ourPoster": "Our Poster",
    "hci.ourTeam": "Our team",
    "hci.dateLabel": "Date:",
    "hci.teamMembersLabel": "Team members:",
    "hci.dateRange": "May 2016 - May 2016",
    "hci.teamMembers": "Lan Zhang, Rocio Perez Cano and Yuanyuan Li",
    "hci.closeProject": "Close Project",

    // ProjectSe translations
    "se.title": "Software Engineering Project",
    "se.role": "Class project",
    "se.description":
      "Developed a web application that will enable researchers to run surveys and research investigations online",
    "se.myRole": "Front-end developer",
    "se.skillsUtilized":
      "requirements analysis, rapid prototyping (Axure), front-end development (html, javascript, jade, node.js, bootstrap, mongoDB)",
    "se.roleLabel": "My role:",
    "se.skillsLabel": "Skills utilized:",
    "se.swDevProcess": "Software development process",
    "se.feasibility": "Feasibility study and planning",
    "se.feasibilityDesc":
      "Defined the aim of this project is to develop the video hosting and file system component for the MERID project, which is studying creativity in orchestras, and to integrate this component with the rest of the MERID system.",
    "se.feasibilityDocs": "-Documents:",
    "se.requirements": "Requirements",
    "se.requirementsDesc":
      "Established the system's functionality, constraints, and goals by consultation with the client.",
    "se.requirementsNote":
      "Used iterative rapid prototypes to further communicate with client. Gained the client's high appreciation for the prototype design.",
    "se.documentsLabel": "Documents:",
    "se.seeDemo": "See Demo",
    "se.systemDesign": "System and Program Design",
    "se.implementation": "Implementation (coding)",
    "se.implementationDesc":
      "Iteratively developed and implemented each of the core features.",
    "se.launchApp": "Launch App",
    "se.systemDesignTitle": "System design",
    "se.dataModel": "Data Model",
    "se.useCases": "Use Cases",
    "se.prototypes": "Prototypes",
    "se.dateLabel": "Date:",
    "se.clientLabel": "Client:",
    "se.teamMembersLabel": "Team members:",
    "se.dateRange": "September 2015 - December 2015",
    "se.client":
      "Graeme Bailey, Computer Science Department; Celine Brass, Technical Advisor and Liaison",
    "se.teamMembers":
      "Yuanyuan Li (Maggie), Chenxi Su, Lucas Derraugh, Laurence Rosenzweig, Darcy A. Branchini, Chen Fang, Vinita Gogate",
    "se.closeProject": "Close Project",

    // ProjectFitCat translations
    "fitcat.title": "iOS Design and Development",
    "fitcat.role": "Cornell Feline Health Project",
    "fitcat.description":
      "Developed a mobile application for Cornell Feline Health Center to improve the health and well-being of cats",
    "fitcat.myRole": "UI Designer and iOS Developer",
    "fitcat.skillsUtilized":
      "programming on the iOS platform, UI design, and UX design",
    "fitcat.roleLabel": "My role:",
    "fitcat.skillsLabel": "Skills utilized:",
    "fitcat.requirements": "Requirements",
    "fitcat.requirementsDesc1":
      "The Cornell Feline Health Center is dedicated to improving the health and well-being of cats everywhere by: (1) educating veterinary professionals, cat owners, breeders, and conservationists about feline health by providing timely medical information and by promoting public understanding and awareness of feline issues; (2) finding ways of preventing and curing diseases of cats by conducting and sponsoring breakthrough feline health studies; and (3) aiding veterinarians when new or unknown feline diseases occur.",
    "fitcat.requirementsDesc2":
      "FitCat Weight Loss is a four-step app with following steps: (1) plan the weight loss program, (2) help the owner shop for food to meet the plan, (3) record what the cat eats, and (4) track actual vs. planned weight loss. The goal of this project was to implement the planning step only. The planning step involves: (1) teaching the owner how to judge their cat's body condition (a visual/tactile measure of the cat's degree of obesity), (2) getting the actual body condition from the owner, (3) using the cat's actual weight and the recommended safe weight loss rate to project the goal weight, the time to reach the goal, and the maximum calories and grams of fat per day for the cat to achieve the goal in the given time.",
    "fitcat.prototypesV1": "Prototypes version 1",
    "fitcat.prototypesDemo": "Prototypes demo",
    "fitcat.prototypesV2": "Prototypes version 2",
    "fitcat.prototypesV2Desc":
      "In the iterative prototypes, we changed the UI design to make it consistent with the Cornell Feline Center brand.",
    "fitcat.sketch3": "Sketch 3 pictures",
    "fitcat.iosDemo": "iOS Demo-Real App",
    "fitcat.presentationPoster": "Presentation Poster",
    "fitcat.ourTeam": "Our Team",
    "fitcat.dateLabel": "Date:",
    "fitcat.clientsLabel": "Clients:",
    "fitcat.dateRange": "Jan 2016 - May 2016",
    "fitcat.clients":
      "Dr. Bruce Kornreich, College of Veterinary Medicine; Paul Cashman, Founder at History in Motion",
    "fitcat.closeProject": "Close Project",

    // ProjectGiveNext translations
    "givenext.title": "Business Consulting Project",
    "givenext.role": "Client: Donation Startup GiveNext",
    "givenext.description":
      "Collaborated with MBAs to make marketing strategies for the startup GiveNext to attract more donors, and designed a mobile app prototypes",
    "givenext.myRole": "Consultant, UI/UX Designer",
    "givenext.skillsUtilized":
      "marketing and branding analysis, marketing research and competitive analysis, UI and UX design, design thinking approach to strategy, marketing and product development",
    "givenext.roleLabel": "My role:",
    "givenext.skillsLabel": "Skills utilized:",
    "givenext.aboutClient": "About the client",
    "givenext.clientDesc":
      "GiveNext is eTrade™ for giving. GiveNext is a mobile and online platform where donors can transact and manage their charitable giving in partnership with the causes they care about. Once the portfolio of charities is compiled to a donor's account and the donor's payment information is entered once, GiveNext becomes the easiest way to give. Donors have instant access to their giving tax receipt and a mobile history of their charitable support.",
    "givenext.aboutClientDesc":
      "GiveNext is eTrade™ for giving. GiveNext is a mobile and online platform where donors can transact and manage their charitable giving in partnership with the causes they care about. Once the portfolio of charities is compiled to a donor's account and the donor's payment information is entered once, GiveNext becomes the easiest way to give. Donors have instant access to their giving tax receipt and a mobile history of their charitable support.",
    "givenext.giveNextHome": "GiveNext Home",
    "givenext.ourActivities": "Our activities",
    "givenext.activity1":
      "Research the charitable giving market and understand the donor and nonprofit behaviors and decision-making",
    "givenext.activity2":
      "Conduct a competitive analysis of the online giving (payment processing) market",
    "givenext.activity3":
      "Identify key product benefits for target markets, and recommend key product features and enhancements to next version of product",
    "givenext.activity4":
      "Create a marketing plan and branding strategy that lead to introduction of GiveNext to the charitable giving and nonprofit industry",
    "givenext.prototypesDemo": "Prototypes Demo",
    "givenext.ourTeam": "Our team",
    "givenext.dateLabel": "Date:",
    "givenext.clientLabel": "Client:",
    "givenext.teamMembersLabel": "Team members:",
    "givenext.dateRange": "Jan 2016 - May 2016",
    "givenext.client": "Daniel J Mansoor, Founder & CEO of GiveNext.com",
    "givenext.teamMembers":
      "Jess Roth, Han-Eol (Ryan) Jang, Yuanyuan (Maggie) Li, Keyan (Kelly) Li, Tejaswini (TJ) Marathe",
    "givenext.closeProject": "Close Project",

    // ProjectAiAssistant translations
    "aiassistant.title": "AI Profile Assistant",
    "aiassistant.role": "Personal project · Sole designer & engineer",
    "aiassistant.tagline":
      "Most engineers would reach for a vector database and RAG. I proved my entire profile fits in under 1% of the context window — and deliberately built the simpler thing.",
    "aiassistant.overview":
      'An AI that answers recruiters\' questions — "What data platforms has she built?", "What\'s her leadership style?" — grounded strictly in my real profile, in text and in my own cloned voice (English & Chinese).',
    "aiassistant.statCost": "$3.65",
    "aiassistant.statCostLabel": "running cost / month",
    "aiassistant.statContext": "<1%",
    "aiassistant.statContextLabel": "of context window used",
    "aiassistant.statLines": "~900",
    "aiassistant.statLinesLabel": "lines of RAG code removed",
    "aiassistant.statVoice": "My voice",
    "aiassistant.statVoiceLabel": "AI-cloned · EN & ZH",
    "aiassistant.roleLabel": "Role",
    "aiassistant.myRole": "Sole designer & engineer — full-stack, AI, infra",
    "aiassistant.stackLabel": "Stack",
    "aiassistant.stack":
      "Python · FastAPI · Anthropic Claude · ElevenLabs · AWS Lambda + API Gateway (SAM)",
    "aiassistant.githubLabel": "Code",
    "aiassistant.tryItLive":
      "Try it live → the chat widget on this site is this exact backend.",
    "aiassistant.decisionTitle": "The Key Decision: CAG, not RAG",
    "aiassistant.decisionIntro":
      "Profile = ~37KB (~1,500 tokens). Claude's window = 200,000 tokens. There's nothing to \"retrieve from\" — the whole corpus already fits.",
    "aiassistant.ragHead": "RAG — the textbook default",
    "aiassistant.ragPoint1":
      "Vector DB, embeddings, chunking, similarity tuning",
    "aiassistant.ragPoint2": "Built for huge corpora that can't fit in context",
    "aiassistant.ragPoint3": "A mis-ranked chunk → wrong answer, hard to debug",
    "aiassistant.ragVerdict": "Overkill here",
    "aiassistant.cagHead": "CAG — what I shipped",
    "aiassistant.cagPoint1": "Full profile in the system prompt, every request",
    "aiassistant.cagPoint2":
      "Anthropic prompt caching → processed once, not per call",
    "aiassistant.cagPoint3":
      "Claude sees 100% of context → no retrieval failures",
    "aiassistant.cagVerdict": "Simpler · cheaper · grounded",
    "aiassistant.archTitle": "Architecture",
    "aiassistant.archApp":
      "FastAPI on Lambda — endpoints, request validation, CORS, cost guards",
    "aiassistant.archAi":
      "Claude (Haiku 4.5) — full profile in a prompt-cached system prompt",
    "aiassistant.archSession":
      "Session manager — per-session multi-turn conversation history",
    "aiassistant.archServices":
      "ElevenLabs — TTS playback + real-time voice conversation, EN/ZH auto-detect",
    "aiassistant.featuresTitle": "Features",
    "aiassistant.featuresGrounded":
      "Grounded Q&A — answers constrained to my real profile; refuses to speculate.",
    "aiassistant.featuresMultiturn":
      "Multi-turn — session history keeps follow-up questions in context.",
    "aiassistant.featuresVoice":
      "Speaks in my own voice — an ElevenLabs clone of my voice and accent reads back every answer, in both English and Chinese (auto-detected).",
    "aiassistant.featuresRealtime":
      "Real-time voice — spoken back-and-forth, not just playback.",
    "aiassistant.featuresCost":
      "Cost controls — request hard-limits, throttling, and budget alerts.",
    "aiassistant.highlightsTitle": "Engineering Highlights",
    "aiassistant.highlightsRightsized":
      "Right-sized architecture — chose CAG over RAG; no database the project didn't need.",
    "aiassistant.highlightsCaching":
      "Prompt caching — the largest part of every prompt is processed once, not per request.",
    "aiassistant.highlightsDegradation":
      "Graceful degradation — voice is optional; no single dependency takes the service down.",
    "aiassistant.highlightsPrivacy":
      "Privacy by design — the prompt enforces boundaries; safe for any audience.",
    "aiassistant.highlightsIac":
      "Infrastructure as code — full serverless stack in one SAM template, one-command deploy.",
    "aiassistant.highlightsSecrets":
      "Secrets hygiene — keys in env / SAM params, never in code; uv, mypy, black, pytest.",
    "aiassistant.deployTitle": "Deployment & Cost",
    "aiassistant.deployChip1": "Serverless · AWS Lambda + API Gateway",
    "aiassistant.deployChip2": "~$3.65 / month",
    "aiassistant.deployChip3": "Budget alert @ $5",
    "aiassistant.deployChip4": "10 req/s throttle · 900K/mo cap",
    "aiassistant.deployChip5": "2–3s cold start",
    "aiassistant.deployChip6": "1-command SAM deploy",
    "aiassistant.imageCaption": "The chat widget — live on this site",
    "aiassistant.dateLabel": "Date",
    "aiassistant.date": "March 2026",
    "aiassistant.closeProject": "Close Project",

    // ProjectDag translations
    "dag.title": "DAG Workflow Engine",
    "dag.role": "Personal project · Sole designer & engineer",
    "dag.tagline":
      "A lightweight Airflow / Temporal: design a workflow visually, get DAG-correctness guarantees before it runs, then execute independent branches with real concurrency.",
    "dag.overview":
      "Hand-written scripts are brittle; Airflow and Temporal are overkill for many jobs. This engine hits the middle — author visually, validate the graph, run independent steps in parallel, and debug interactively when something breaks.",
    "dag.statFullstack": "Full-stack",
    "dag.statFullstackLabel": "visual builder + async engine",
    "dag.statLight": "Lightweight",
    "dag.statLightLabel": "single process, no heavy infra",
    "dag.statExtensible": "Extensible",
    "dag.statExtensibleLabel": "node types via JSON config",
    "dag.statResilient": "Resilient",
    "dag.statResilientLabel": "retries resume from failure",
    "dag.roleLabel": "Role",
    "dag.myRole":
      "Sole designer & engineer — full-stack, graph algorithms, async execution, infra",
    "dag.stackLabel": "Stack",
    "dag.stack":
      "FastAPI · Python 3.13 (async) · SQLAlchemy + Alembic · React + TypeScript · React Flow · TanStack Query · PostgreSQL · httpx · Docker Compose",
    "dag.scaleLabel": "Scale",
    "dag.scale":
      "~2.4K lines backend Python, ~5.6K lines frontend TS/TSX, 6 PostgreSQL tables — small enough to reason about end-to-end, large enough to show real engineering.",
    "dag.githubLabel": "Code",
    "dag.editorCaption": "The visual workflow editor (React Flow canvas)",
    "dag.archTitle": "Architecture",
    "dag.archFlowUi": "design · monitor · step-debug",
    "dag.archFlowApi": "validate · schedule · execute",
    "dag.archFlowDb":
      "6 tables: workflows, nodes, edges, runs, node_runs, logs",
    "dag.archNodeIntro": "Four node types (config in flexible JSON columns):",
    "dag.archTwoPhase":
      "Two-phase model — Definition (the static graph) is separated from Execution (a record per run). That split is what enables retry-from-failure, run history, and concurrent runs of the same workflow.",
    "dag.workflowCaption":
      "A built workflow — parallel API checks branching on the combined result",
    "dag.deepDiveTitle": "Technical Deep Dives",
    "dag.deepValidation":
      "DAG validation — seven graph checks before save/run, the highlight being cycle detection via three-color DFS. Catching a cycle at design time beats failing mid-execution.",
    "dag.deepScheduler":
      "Concurrent scheduler — a ready-set asyncio loop runs all ready nodes via asyncio.gather(), bounded by a semaphore (10) so wide fan-out won't overwhelm downstream APIs or the DB pool.",
    "dag.deepBranching":
      "Conditional branching — a node is ready only when all active parent edges succeed; edges on untaken branches are excluded, and unreached nodes are swept to skipped.",
    "dag.deepRetry":
      "Retry from failure — successful node outputs persist; only the failed node and its subtree reset, so you don't re-charge a payment API because step 7 of 9 failed.",
    "dag.deepStep":
      "Step-through debugging — the executor pauses after each node and polls for a signal; the UI drives it with /step and /continue, with graceful cancellation.",
    "dag.deepHttp":
      "Resilient HTTP nodes — httpx with classified retry (retry timeouts/5xx, not 4xx); a sandbox mode swaps live calls for mocks so workflows test with zero external deps.",
    "dag.runCaption":
      "Run detail — per-node status, logs, and active vs. skipped paths",
    "dag.tradeoffsTitle": "Design Decisions & Tradeoffs",
    "dag.tradeoffExecutor":
      "Single-process async executor — simple to debug; swap to Celery/RabbitMQ workers for horizontal scale without touching the graph logic.",
    "dag.tradeoffPolling":
      "Polling (2s) over WebSockets — faster to build and good enough; WebSocket push is the migration path for real-time UI.",
    "dag.tradeoffTemplates":
      "{{var}} template strings, not arbitrary expressions — no injection risk; a sandboxed evaluator only if richer logic is ever needed.",
    "dag.tradeoffScheduler":
      "Ready-set loop over a precomputed topological sort — required because branches prune the graph dynamically at runtime.",
    "dag.testingTitle": "Testing Strategy",
    "dag.testingDesc":
      "Five focused critical-path tests targeting what would actually break in production — concurrency, failure handling, recovery — not line coverage:",
    "dag.testSequential": "Sequential execution succeeds.",
    "dag.testParallel":
      "Parallel execution — 3 independent branches run concurrently (asserts the API calls overlap).",
    "dag.testFailure": "A failed node halts execution correctly.",
    "dag.testRetry": "Retry-from-failure preserves successful node outputs.",
    "dag.testInactive": "Inactive branch edges don't block downstream nodes.",
    "dag.dateLabel": "Date",
    "dag.date": "March 2026",
    "dag.closeProject": "Close Project",

    // ProjectRapid translations
    "rapid.title": "Rapid Prototyping and Physical Computing",
    "rapid.role": "Class project",
    "rapid.description":
      "Learned modern rapid prototyping techniques such as laser cutting, 3D printing and microcontroller programming (such as the Arduino system)",
    "rapid.myRole": "Programmer",
    "rapid.skillsUtilized":
      "microcontrollers (Photon), CAD program (AutoDesk Inventor), building prototypes with a laser cutter, and 3D printer",
    "rapid.roleLabel": "My role:",
    "rapid.skillsLabel": "Skills utilized:",
    "rapid.design1": "Design 1: Interactive Design",
    "rapid.design1Desc":
      "The small moving car is a design for a fun game. In the game, players can set a target distance by the potentiometer. The player then can use the button to control the mini car to the target distance. There are two different ways to use the button. One is the quick and short click, and the other is the longer click. The quick and short click can switch the mini car run or stop. The longer click can switch the direction of the car (moving forward or moving backward). When the mini car is far away from the target distance, the 3-color LED light will turn green. When the car is at the target place, the blue light will be on. But if the car is too close to the target object, the red light will be on.",
    "rapid.interactiveDemo": "Interactive Design: Demo",
    "rapid.design2": "Design 2: Interactive Design",
    "rapid.design2Desc":
      'My second design project is a box made by a 2D laser cutter. The box can be triggered to open by the correct input code. The default code is "longer click, shorter click, and longer click". It also has a speaker to tell you if your input code is correct or not. When the system start to work, the speaker will play music. Then you can start to input the opening code. If you click in wrong actions, a speaker will play error sound. If you click code correctly, the speaker will play music and the box will open. After seconds, the box will close automatically. The status of the input code can also be seen in console if you connect it.',
    "rapid.2dPrinterDemo": "2D printer Design: Demo",
    "rapid.design3": "Design 3: Puzzle 3D",
    "rapid.design3Desc1":
      "In the third design assignment, I made a 3D puzzle Phillipe Dubois' Burr. This puzzle has six different parts that can be assembled together. The design idea comes from a",
    "rapid.design3Desc2":
      "In this design, I used Inventor to draw 2D sketches and 3D objects that could be printed in the 3D printer",
    "rapid.3dPrinterDemo": "3D printer Design: Demo",
    "rapid.finalProject": "Final Project: Kinetic Art (Group Work)",
    "rapid.finalProjectDesc1":
      "Kinetic art is an art form that conveys feelings to viewers through movement. We built a sculpture with kinetic particles in this project. The original idea is similar to the kinetic rain in Singapore's Changi Airport.",
    "rapid.finalProjectDesc2":
      "Our final design had 3 rows of hanging particles that could move into several programmed patterns. These patterns could follow the sine wave, cosine wave, and other shapes. We used a stepping motor to control the position of the particles. We also had 9 three-color LED lights that could change color, helping the acrylic particles look beautiful.",
    "rapid.presentationPoster": "Presentation Poster",
    "rapid.kineticDemo": "Kinetic Art: Demo",
    "rapid.dateLabel": "Date:",
    "rapid.date": "May 2016",
    "rapid.closeProject": "Close Project",

    // ProjectCscw translations
    "cscw.title": "Computer Supported Collaboration Research",
    "cscw.role": "Facilitation Collaboration Research Lab",
    "cscw.description":
      "Designed efficient and effective processes for collaborative teams. In this research experience, I improved my research and writing skills",
    "cscw.myRole": "UX Researcher",
    "cscw.skillsUtilized":
      "user experience research, data analysis (SPSS), php web development and writing skills",
    "cscw.roleLabel": "My role:",
    "cscw.skillsLabel": "Skills utilized:",
    "cscw.researchExperience": "Research Experience",
    "cscw.experience1":
      "Constructed collaborative models by reviewing 30+ papers from IS top journals",
    "cscw.experience2": "Identified research topics independently",
    "cscw.experience3": "Interviewed 100+ people after experiments",
    "cscw.experience4":
      "Analyzed 100+ transcripts and self-learned statistic analysis methods",
    "cscw.experience5": "Self-developed tools:",
    "cscw.publications": "Publications",
    "cscw.publication1":
      '"Application of a Novel Collaboration Engineering Method for Learning Design: A Case Study", SSCI journal: British Journal of Educational Technology, 2015',
    "cscw.publication2":
      '"Can We Solve Low Participation, Distraction, and Inefficiency? A Case Study of Distributed Collaborative Learning in Industries", 48th Hawaii International Conference on System Sciences (#2 in information system), 2015',
    "cscw.publication3":
      '"Collaboration Information System Adoption in Hospitality", 18th Pacific Asia Conference on Information Systems, 2014',
    "cscw.publication4":
      '"Easy Collaboration Process Support System Design for Student Collaborative Group Work", 47th Hawaii International Conference on System Sciences, 2014',
    "cscw.publication5":
      '"An Online Collaboration Process Design for the Student Organization", Journal of Theoretical and Applied Information Technology, 2012',
    "cscw.designBrainstorming": "Design brainstorming",
    "cscw.firstSketch": "First Sketch",
    "cscw.finalDesign": "Final Design",
    "cscw.responsiveDev": "Responsive development",
    "cscw.responsiveDevelopment": "Responsive development",
    "cscw.techStack": "PHP, Apache, Windows, MySQL",
    "cscw.launchedApp": "Launched App",
    "cscw.seeDemo": "See Demo",
    "cscw.seeDetails": "See details",
    "cscw.viewPdf": "View pdf",
    "cscw.dateLabel": "Date:",
    "cscw.dateRange": "October 2012 - October 2014",
    "cscw.closeProject": "Close Project",

    // ProjectDataProduct translations
    "dataproduct.title": "Designing Data Products with MBAs",
    "dataproduct.role": "Class project",
    "dataproduct.description":
      "Learned to use Regression, KNN-cluster, Naive Bayes and Decision Tree to solve business problems and proposed a data product design",
    "dataproduct.myRole": "Developer, Data scientist",
    "dataproduct.skillsUtilized":
      "Data analysis and visualization, presentation",
    "dataproduct.roleLabel": "My role:",
    "dataproduct.skillsLabel": "Skills utilized:",
    "dataproduct.seeDemo": "See Demo",
    "dataproduct.dateLabel": "Date:",
    "dataproduct.teamMembersLabel": "Team members:",
    "dataproduct.closeProject": "Close Project",
    "dataproduct.dateRange": "March 2016 - April 2016",
    "dataproduct.teamMembers":
      "Jack Zoephel, Tyler Done, Sowmya Ballakur, Ryan Armstrong, Yuanyuan Li",

    // ProjectNonprofit translations
    "nonprofit.title": "Non-profit Business Solution",
    "nonprofit.role": "Hackathon project",
    "nonprofit.description":
      "Designed a solution to help raise awareness about food accessibility",
    "nonprofit.myRole": "Developer",
    "nonprofit.skillsUtilized":
      "agile web development, business analysis and presentation",
    "nonprofit.roleLabel": "My role:",
    "nonprofit.skillsLabel": "Skills utilized:",
    "nonprofit.targetProblems": "Target problems",
    "nonprofit.ourSolutions": "Our solutions",
    "nonprofit.problemDesc":
      "Family and Children Services of Ithaca and food insecurity issues",
    "nonprofit.problemDescription":
      "Family and Children Services of Ithaca has witnessed there is a lack of basic resources for low income individuals and families and people in poverty to feed themselves or their families on a daily basis. Many individuals sit or stand with a sign requesting money for food, for housing, or basic survival. Community members sometimes respond with money and / or food. The act of holding a sign can degrade the self-esteem of individuals, and it is not a welcome sight to members of the community. The organization would like to see an alternative way for individuals to donate and receive the funds they need to eat.",
    "nonprofit.solutionDesc":
      "Pro Bonobos is a program designed to help raise awareness about food insecurity. It would help low income individuals and families in poverty to feed themselves on a daily basis",
    "nonprofit.solutionDescription":
      "Pro Bonobos is a program designed to help raise awareness about food insecurity. It would help low income individuals and families in poverty to feed themselves on a daily basis",
    "nonprofit.seeDemo": "See Demo",
    "nonprofit.dateLabel": "Date:",
    "nonprofit.clientLabel": "Client:",
    "nonprofit.teamMembersLabel": "Team members:",
    "nonprofit.closeProject": "Close Project",
    "nonprofit.date": "November 2015",
    "nonprofit.client": "Tammy Baker, Family and Children Services of Ithaca",
    "nonprofit.teamMembers":
      "Karen Tan, Jiyoung Oh, Yuanyuan Li, Jennifer Lee, Greta Jenkins, Brett Granum",

    // Chat Widget
    "widget.title": "Yuanyuan Li",
    "widget.subtitle": "Professional Profile",
    "widget.placeholder": "Ask about Yuanyuan's background...",
    "widget.welcomeMessage":
      "Welcome! I'm here to help you learn about Yuanyuan Li's professional background, experience, and expertise. Ask me anything about her work history, projects, skills, or leadership style.",
    "widget.newChat": "New chat",
    "widget.toggleSidebar": "Toggle sidebar",
    "widget.toggleTheme": "Toggle theme",
    "widget.close": "Close",
    "widget.send": "Send message",
    "widget.loading": "Loading...",
    "widget.openChat": "Open chat",
    "widget.suggestedHeader": "Try asking:",
    "widget.suggested.about": "Tell me about Yuanyuan",
    "widget.suggested.dataSystems": "What data systems has she built?",
    "widget.suggested.aws": "What's her experience with AWS?",
    "widget.suggested.leadership": "Describe her leadership style",
    "widget.suggested.projects": "What has she worked on at Two Sigma?",
    "widget.sources": "Sources",
    "widget.error": "Sorry, something went wrong. Please try again.",

    // Voice playback
    "widget.voice.play": "▶ Listen",
    "widget.voice.loading": "Generating audio...",
    "widget.voice.pause": "⏸ Pause",
    "widget.voice.resume": "▶ Resume",
    "widget.voice.replay": "↻ Replay",
    "widget.voice.error": "Audio unavailable",
    "widget.voice.tooLong": "This response is too long for audio playback.",
    "widget.voice.tooMany": "Too many audio requests. Please try again later.",
    "widget.voice.quotaExceeded": "Audio unavailable this month.",
    "widget.voice.disclosure": "Voice is AI-generated.",
    "widget.voice.autoPlay": "Auto-play audio",
    "widget.voice.aria.play": "Listen to this response",
    "widget.voice.aria.pause": "Pause audio playback",
    "widget.voice.aria.resume": "Resume audio playback",
    "widget.voice.aria.replay": "Replay audio",
    "widget.voice.aria.toggle": "Toggle auto-play audio",

    // Voice conversation
    "voice.call.start": "Talk to Yuanyuan's AI",
    "voice.call.end": "End call",
    "voice.call.retry": "Try again",
    "voice.call.close": "Close",
    "voice.call.connecting": "Connecting...",
    "voice.call.ai_speaking": "AI is speaking",
    "voice.call.listening": "Listening...",
    "voice.call.transcript_label": "Live transcript:",
    "voice.call.ended": "Call ended",
    "voice.call.in_call": "In call",
    "voice.call.error.mic_denied":
      "Microphone access denied. Please allow mic access and try again.",
    "voice.call.error.mic_unavailable": "Microphone not available.",
    "voice.call.error.session_failed":
      "Could not start voice session. Please try again.",
    "voice.call.error.connection_lost": "Voice connection lost.",
    "voice.call.aria.start": "Start voice conversation",
    "voice.call.aria.end": "End voice conversation",
  },
  zh: {
    // Navigation
    "nav.experience": "工作经历",
    "nav.portfolio": "项目作品",
    "nav.skills": "技能专长",
    "nav.contact": "联系我",
    "nav.close": "关闭",
    "nav.skipToMain": "跳转到主要内容",
    "project.nav.back": "全部项目",
    "project.nav.prev": "上一个",
    "project.nav.next": "下一个",
    "aria.projectNav": "项目导航",

    // Home section
    "home.greeting": "你好，我是",
    "home.name": "李瑗瑗",
    "home.title1": "软件工程副总裁",
    "home.title2": "全栈 + 数据平台工程",
    "home.title3": "10年工程领导经验",
    "home.downloadResume": "下载简历",
    "home.loading": "加载中...",

    // ARIA labels
    "aria.portfolioWebsite": "个人作品网站",
    "aria.portfolioHomepage": "个人作品主页",
    "aria.nameTitle": "李瑗瑗，软件工程经理",
    "aria.downloadResume": "下载李瑗瑗的简历PDF",
    "aria.socialLinks": "社交媒体链接",
    "aria.linkedinProfile": "访问李瑗瑗的LinkedIn个人资料",
    "aria.githubProfile": "访问李瑗瑗的GitHub个人资料",
    "aria.facebookProfile": "访问李瑗瑗的Facebook个人资料",
    "aria.mainNavigation": "主导航菜单",
    "aria.secondaryNavigation": "次要导航菜单",
    "aria.portfolioSections": "作品集版块",
    "aria.viewExperience": "查看工作经历版块",
    "aria.viewPortfolio": "查看项目作品版块",
    "aria.viewSkills": "查看技能专长版块",
    "aria.viewContact": "查看联系我版块",
    "aria.navigateToExperience": "导航到工作经历版块",
    "aria.navigateToPortfolio": "导航到项目作品版块",
    "aria.navigateToSkills": "导航到技能专长版块",
    "aria.navigateToContact": "导航到联系我版块",
    "aria.closeNavigation": "关闭导航并返回主页",
    "aria.experienceContent": "工作经历内容",
    "aria.portfolioContent": "项目作品内容",
    "aria.skillsContent": "技能专长内容",
    "aria.contactContent": "联系我内容",

    // Language switcher
    "lang.switchTo": "切换到",
    "lang.english": "English",
    "lang.chinese": "中文",

    // Experience page
    "exp.profile": "个人简介",
    "exp.experience": "工作经历",
    "exp.education": "教育背景",
    "exp.awards": "获奖情况",
    "exp.name": "姓名",
    "exp.location": "所在地",
    "exp.email": "邮箱",
    "exp.skype": "LinkedIn",
    "exp.interest": "求职意向",
    "exp.current": "至今",
    "exp.contactMessage": "欢迎联系我讨论有趣的项目和工作机会",

    // Skills page
    "skills.title": "技能专长",
    "skills.technical": "全栈与 AI",
    "skills.cloud": "云平台与数据基础设施",
    "skills.knowhow": "工程管理",

    // Contact page
    "contact.getInTouch": "联系我",
    "contact.location": "所在地",
    "contact.email": "邮箱",
    "contact.contactMessage": "欢迎联系我讨论有趣的项目和工作机会",

    // Experience page detailed content
    "exp.profileDesc1":
      "我拥有管理学、经济学、计算机科学和数据科学的多元背景，像拼图一样让我能从多个角度理解问题、解决问题。平时最让我兴奋的事，就是把一个创意变成“看得见、摸得着”的东西：网页设计与开发、UI/UX优化、还有数据分析和可视化，统统来者不拒！我热爱折腾，也享受把复杂的东西变得好看又好用。技术，对我来说就像魔法——写下几行代码，就能创造一个新世界！",
    "exp.profileDesc2":
      "我在康奈尔大学获得了信息科学硕士学位，专注于人机交互(HCI)设计。我对运用所学知识构建改善用户体验的界面充满好奇心。我精通设计、构建和测试流程，已将许多项目从初始草图带到生产环境，无论是移动应用还是完整的网络应用程序。",
    "exp.personalInfo.name": "李瑗瑗 (Maggie Li)",
    "exp.personalInfo.location": "纽约大都会区",
    "exp.personalInfo.email": "yuanyuanli2022@gmail.com",
    "exp.personalInfo.skype": "linkedin.com/in/yuanyuanmaggie",
    "exp.personalInfo.interest":
      "VP Engineering, Software Engineering Manager, Data Platform",

    // Portfolio page
    "portfolio.title": "项目作品",
    "portfolio.subtitle": "近期作品",
    "portfolio.viewProject": "查看项目",
    "portfolio.seeDemo": "查看演示",
    "portfolio.sourceCode": "源代码",
    "portfolio.backToPortfolio": "返回作品集",
    "portfolio.interestedInMe": "对我感兴趣？",

    // Portfolio filters
    "portfolio.filter.all": "全部",
    "portfolio.filter.work": "工作经历",
    "portfolio.filter.side": "业余项目",
    "portfolio.filter.school": "研究与学术项目",

    // Project titles and descriptions
    "project.venn.title": "Venn (Two Sigma)",
    "project.venn.desc": "软件工程经理",
    "project.aiassistant.title": "AI 个人助手",
    "project.aiassistant.desc": "无服务器对话与语音聊天机器人（CAG）",
    "project.dag.title": "DAG 工作流引擎",
    "project.dag.desc": "可视化工作流编排 + 并发 DAG 执行器",
    "project.jet.title": "Jet.com (沃尔玛)",
    "project.jet.desc": "前端工程师",
    "project.supply.title": "SupplyHouse.com",
    "project.supply.desc": "网页开发",
    "project.yumso.title": "Yumso Inc",
    "project.yumso.desc": "网页开发",
    "project.d3.title": "数据驱动的网页应用 (D3.js)",
    "project.d3.desc": "数据可视化",
    "project.hci.title": "人机交互设计项目",
    "project.hci.desc": "UI/UX 设计",
    "project.se.title": "软件工程客户项目",
    "project.se.desc": "网页开发",
    "project.fitcat.title": "康奈尔猫科健康中心项目",
    "project.fitcat.desc": "iOS 开发",
    "project.givenext.title": "捐赠创业商业解决方案",
    "project.givenext.desc": "UI/UX 设计",
    "project.cscw.title": "计算机支持协作研究",
    "project.cscw.desc": "用户体验研究",
    "project.dataproduct.title": "设计数据产品 MBA 项目",
    "project.dataproduct.desc": "数据分析和可视化",
    "project.nonprofit.title": "非营利组织商业解决方案（黑客马拉松）",
    "project.nonprofit.desc": "网页开发",
    "project.rapid.title": "快速原型和物理计算",
    "project.rapid.desc": "硬件和原型开发",

    // Experience details - Two Sigma
    "exp.twosigma.bullet1":
      "从零开始组建了一支高效的数据工程团队，从0人发展到4人，建立了以主人翁意识、协作和影响力为核心的强大团队文化。",
    "exp.twosigma.bullet2":
      "带领团队交付了一个全面的数据平台，支持整个数据生命周期，包括数据摄取、转换、存储和服务，使组织能够利用数据进行战略决策。",
    "exp.twosigma.bullet3":
      "主导开发了一个数据产品，为组织提供统一的数据视图，提高了内部利益相关者的数据可访问性和可用性。",
    "exp.twosigma.bullet4":
      "倡导在数据工程中采用最佳实践，包括数据质量、治理和安全性，确保数据可靠、安全并符合行业标准。",
    "exp.twosigma.bullet5":
      "与跨职能团队合作，包括产品经理、数据科学家和业务利益相关者，将数据工程工作与业务目标保持一致，并提供高影响力的数据解决方案。",
    "exp.twosigma.bullet6":
      "推动团队内敏捷方法论的实施，改善了项目交付时间并提高了团队生产力。",
    "exp.twosigma.bullet7":
      "指导和培训团队成员，为技术挑战和职业发展提供指导，在团队内培养学习和成长的文化。",
    "exp.twosigma.bullet8":
      "使用AWS服务等现代技术实施健壮的数据管道，确保高效的数据处理和实时分析能力。",
    "exp.twosigma.bullet9":
      "因卓越的领导力和影响力而受到认可，因对组织数据战略和执行的贡献而获得高级领导层的赞誉。",

    // Experience details - Jet.com
    "exp.jet.bullet1":
      "领导一个五人小组，负责用React和Redux重建订单历史流程。负责规划、界定范围并改进团队在每个敏捷冲刺中的工作流程。",
    "exp.jet.bullet2": "设计了灵活的分析库，帮助业务分析师团队跟踪客户行为。",
    "exp.jet.bullet3":
      "维护的功能包括客户结账、搜索建议和促销。运用前端技术栈的全套工具，包括pug (jade)、Stylus和express.js（用于开发）以及Cucumber、Mocha和WebdriverIO（用于测试）。",
    "exp.jet.bullet4":
      "与产品设计师、产品经理、营销分析师和API工程师合作交付功能。",
    "exp.jet.bullet5":
      "领导跨职能团队在Jet内部黑客马拉松（Jet创新时间）中获得头奖。该想法已发展成为Jet的新产品线。",
    "exp.jet.bullet6":
      "独立开发了C#前端负载测试套件，用于测试节假日高峰需求。与业务分析师、DevOps和其他上游团队合作，将订单系统扩展到能够处理预期业务目标3倍的负载。",
    "exp.jet.bullet7":
      "在前端可访问性、跨浏览器兼容性、搜索引擎优化（SEO）和网络性能优化方面经验丰富。",
    "exp.jet.bullet8":
      "参与团队的轮值值班工作，排除实时生产问题。该系统支持数十万笔月度交易。",
    "exp.jet.bullet9": "Jet聚会演讲者",

    // Experience details - SupplyHouse
    "exp.supply.bullet1":
      "使用Java（以及FreeMarker）、JavaScript、HTML5、CSS3、jQuery和Bootstrap开发功能完整且响应式的电商网站。",
    "exp.supply.bullet2":
      "创建代码和设计指南，整合来自平面设计师、营销人员和软件工程师的反馈。",
    "exp.supply.bullet3":
      "根据业务需求设计原型，然后将原型转化为具有可重用用户界面（UI）组件的干净的发布就绪页面。",
    "exp.supply.bullet4": "在多个浏览器中测试前端代码以确保跨浏览器兼容性。",
    "exp.supply.bullet5": "使用PHP、MySQL和WordPress管理一系列网站。",

    // Experience details - Yumso
    "exp.yumso.bullet1":
      "使用AngularJS、D3.js和Sass维护、开发和设计我们的创业平台，使用户能够与本地厨师联系。",
    "exp.yumso.bullet2":
      "执行并贡献了全栈网络开发，重点关注前端功能和跨浏览器兼容性。",
    "exp.yumso.bullet3": "使用敏捷开发方法论与虚拟和跨职能团队协作。",

    // Experience details - PwC
    "exp.pwc.bullet1": "与客户携手分析遗留系统的迁移需求。",
    "exp.pwc.bullet2": "开发和测试IT系统规范并维护项目文档。",

    // Experience details - SAP
    "exp.sap.bullet1": "分析客户需求，通过重新设计流程提高客户满意度。",
    "exp.sap.bullet2":
      "与全球IT架构师合作，完成20多份关于SAP ERP集成、HANA内存迁移和系统景观优化（SLO）的报告。",

    // Education details
    "exp.cornell.degree": "信息科学专业硕士",
    "exp.cornell.gpa": "GPA：3.73/4.0",
    "exp.cornell.courses":
      "主要课程：数据库系统、软件工程、网络可视化数据分析、设计数据产品、面向对象编程与数据结构、人机交互设计",

    "exp.uibe.master.degree": "经济学硕士",
    "exp.uibe.master.gpa": "GPA：3.71/4.0",
    "exp.uibe.master.courses":
      "主要课程：高级经济学、系统分析与设计、经济数据分析、研究方法、技术与金融、国际商务",

    "exp.uibe.bachelor.degree": "经济学学士",
    "exp.uibe.bachelor.gpa": "GPA：3.86/4.0，排名1/108",
    "exp.uibe.bachelor.courses":
      "主要课程：电子商务、金融银行学、微观经济学、宏观经济学、网页开发、统计学与会计学",

    // Awards
    "exp.award.guanghua": "光华奖学金（前5%）、一等奖学金（前5%）",
    "exp.award.beijing.graduate": "北京市优秀毕业生",
    "exp.award.academic": "学习优秀奖学金（4/8000+）",
    "exp.award.leadership": "领导力奖学金（6/8000+）",
    "exp.award.national": "国家奖学金（前1%）",
    "exp.award.beijing.merit": "北京市三好学生（前1%）",
    "exp.award.ebusiness":
      '全国大学生电子商务创新、创意及创业挑战赛一等奖暨"最佳创业奖"',

    // ProjectVenn translations
    "venn.title": "Venn (Two Sigma)",
    "venn.role": "副总裁，软件工程经理",
    "venn.bullet1":
      "从零开始组建了一支高效的数据工程团队，从0人发展到4人，建立了以主人翁意识、协作和影响力为核心的强大团队文化。",
    "venn.bullet2":
      "与产品和量化团队密切合作，交付创收的数据产品，包括API平台（Factor、Integration、Analytics）、面向客户的UI功能，以及为私人客户量身定制的可扩展入门框架。",
    "venn.bullet3":
      "推动销售、营销、法务、客户解决方案、设计和产品团队之间的跨职能协调以交付新功能，并担任外部数据供应商集成和事件解决的主要联系人。",
    "venn.bullet4":
      "设计和实施团队运营例程，包括敏捷冲刺节奏、目标设定（OKR）、路线图规划、里程碑跟踪和事件回顾实践。",
    "venn.bullet5":
      "在AWS上扩展ETL平台（Glue、Step Functions、Lambda、S3），提高数据处理可靠性和SLA合规性。",
    "venn.bullet6":
      "为团队提供技术领导和指导，构建跨React/TypeScript前端和Java/Python后端的全栈功能，部署在AWS EKS上。监督设计决策、进行代码审查并确保工程最佳实践。",
    "venn.bullet7":
      "推动招聘、入职和职业发展流程，实现了四次内部晋升和在成长对话中的高参与度。",
    "venn.myRole": "数据平台团队工程经理",
    "venn.skillsUtilized": "ETL开发，项目管理",
    "venn.roleLabel": "我的角色：",
    "venn.skillsLabel": "运用技能：",
    "venn.apiTitle": "Venn API",
    "venn.factorTitle": "Venn因子分析",
    "venn.seeMoreWebsite": "在网站查看更多",
    "venn.seeDemo": "查看Venn演示",
    "venn.dateLabel": "时间：",
    "venn.dateRange": "2018年7月 - 至今",
    "venn.closeProject": "关闭项目",

    // ProjectJet translations
    "jet.title": "Jet.com",
    "jet.role": "前端工程师",
    "jet.bullet1":
      "领导一个五人小组，负责用React和Redux重建订单历史流程。负责规划、界定范围并改进团队在每个敏捷冲刺中的工作流程。",
    "jet.bullet2": "设计了灵活的分析库，帮助业务分析师团队跟踪客户行为。",
    "jet.bullet3":
      "维护的功能包括客户结账、搜索建议和促销。运用前端技术栈的全套工具，包括pug (jade)、Stylus和express.js（用于开发）以及Cucumber、Mocha和WebdriverIO（用于测试）。",
    "jet.bullet4":
      "与产品设计师、产品经理、营销分析师和API工程师合作交付功能。",
    "jet.bullet5":
      "领导跨职能团队在Jet内部黑客马拉松（Jet创新时间）中获得头奖。该想法已发展成为Jet的新产品线。",
    "jet.bullet6":
      "独立开发了C#前端负载测试套件，用于测试节假日高峰需求。与业务分析师、DevOps和其他上游团队合作，将订单系统扩展到能够处理预期业务目标3倍的负载。",
    "jet.bullet7":
      "在前端可访问性、跨浏览器兼容性、搜索引擎优化（SEO）和网络性能优化方面经验丰富。",
    "jet.bullet8":
      "参与团队的轮值值班工作，排除实时生产问题。该系统支持数十万笔月度交易。",
    "jet.bullet9": "Jet聚会演讲者",
    "jet.myRole": "前端开发工程师",
    "jet.skillsUtilized": "React/redux，MVC，前端运维",
    "jet.roleLabel": "我的角色：",
    "jet.skillsLabel": "运用技能：",
    "jet.guestCheckout": "访客结账",
    "jet.meetupTitle": "Jet聚会演讲",
    "jet.meetupSubtitle": "Jet.com的前端测试",
    "jet.hackathon1Title": "Jet创新黑客马拉松",
    "jet.hackathon1Subtitle": "Jetness",
    "jet.hackathon1Github": "Github：",
    "jet.hackathon1Demo": "Jetness黑客马拉松演示",
    "jet.hackathon1Author": "来自",
    "jet.hackathon1Platform": "在",
    "jet.hackathon2Title": "Jet创新黑客马拉松2",
    "jet.hackathon2Subtitle": "与Jet一起烹饪",
    "jet.hackathon2Demo": "黑客马拉松演示",
    "jet.seeMoreWebsite": "在网站查看更多",
    "jet.seeSlide": "查看幻灯片",
    "jet.seeDemoWebsite": "查看演示网站",
    "jet.dateLabel": "时间：",
    "jet.dateRange": "2017年1月 - 2018年7月",
    "jet.closeProject": "关闭项目",

    // ProjectSupply translations
    "supply.title": "SupplyHouse.com",
    "supply.role": "网页（UI/UX）开发工程师",
    "supply.description1":
      "使用HTML5、CSS3、Bootstrap、jQuery、Java、AJAX和FreeMarker以及Sublime Text、Eclipse、SVN和Adobe Photoshop等工具，为电子商务开发功能完整的响应式新网站。",
    "supply.description2":
      "除了开发工作，我还根据业务需求设计原型，然后将原型和设计转化为干净、有效且可发布的页面。因此我能够弥合UI设计与技术实现之间的差距，在双方都发挥积极作用，定义应用程序的外观和工作方式。",
    "supply.myRole": "前端开发工程师",
    "supply.skillsUtilized": "网页开发",
    "supply.roleLabel": "我的角色：",
    "supply.skillsLabel": "运用技能：",
    "supply.projectTitle": "新网站项目",
    "supply.seeNewWebsites": "查看新网站",
    "supply.dateLabel": "时间：",
    "supply.dateRange": "2016年8月 - 2017年1月",
    "supply.closeProject": "关闭项目",

    // ProjectYumso translations
    "yumso.title": "Yumso Inc",
    "yumso.role": "创业公司联合创始人",
    "yumso.description":
      "使用AngularJS、D3.js、Sass、Bootstrap、jQuery等技术开发厨师/管理员/司机网站。我们的应用和网站已经上线并投入生产。我还执行并贡献了全栈网络开发项目，重点关注前端功能、浏览器操作和跨浏览器兼容性。",
    "yumso.myRole": "全栈开发工程师，用户体验设计师",
    "yumso.skillsUtilized":
      "AngularJS，数据可视化，用户体验设计，快速原型，远程协作",
    "yumso.roleLabel": "我的角色：",
    "yumso.skillsLabel": "运用技能：",
    "yumso.aboutCompany": "关于公司",
    "yumso.companyDesc1":
      "Yumso是一个市场平台，您可以在这里探索并订购由社区中的烹饪爱好者制作的正宗美食。Yumso为您带来家的味道，通过独特的监督系统改善食品安全。我们高效的配送服务以较小的成本将新鲜烹制的餐点送到您的门前。随时以您想要的方式享受高质量的食物。",
    "yumso.companyDesc2":
      "您也可以申请成为Yumso的厨师，与其他美食爱好者分享您的家常菜。珍惜您在厨房的时间，同时赚取收入。",
    "yumso.ideasStart": "想法从框架开始",
    "yumso.adminPanel": "管理面板框架草图",
    "yumso.driverWorkflow": "司机工作流程框架草图",
    "yumso.translatedIdeas": "然后将想法转化为响应式网络应用",
    "yumso.seeDemo": "查看演示",
    "yumso.dateLabel": "时间：",
    "yumso.dateRange": "2016年5月 - 至今",
    "yumso.closeProject": "关闭项目",

    // ProjectD3 translations
    "d3.title": "数据驱动的网络应用",
    "d3.role": "课程项目",
    "d3.description":
      "在现代网络应用环境中处理数据。包括使用关系型和非关系型数据库进行数据表示、数据挖掘以发现模式和进行预测，以及用于可视化的图形展示。",
    "d3.myRole": "前端开发工程师，数据科学家",
    "d3.skillsUtilized": "数据挖掘和可视化，d3.js，javascript",
    "d3.roleLabel": "我的角色：",
    "d3.skillsLabel": "运用技能：",
    "d3.project1Title":
      "项目1：2011-2013年美国主要诊断的医院费用分析（静态地图）",
    "d3.project2Title":
      "项目2：康奈尔大学毕业生按州/地区/学院的起薪情况和生活调查（交互式地图）",
    "d3.project3Title": "项目3：患抑郁症的可能性有多大？（响应式地图）",
    "d3.project4Title": "项目4：D3 + React显示旧金山Muni公交实时数据",
    "d3.selectRoute": "选择路线：",
    "d3.hoverDetail": "悬停详细信息并以路线颜色显示信息",
    "d3.seeDemo": "查看演示",
    "d3.dateLabel": "时间：",
    "d3.teamMembersLabel": "团队成员：",
    "d3.project1Date": "2016年2月 - 2016年3月",
    "d3.project1Team": "李瑗瑗，Richard Quan，Akshay Tata",
    "d3.project2Date": "2016年3月 - 2016年4月",
    "d3.project2Team": "Anavi Nahar，Darcy Branchini，李瑗瑗",
    "d3.project3Date": "2016年4月 - 2016年5月",
    "d3.project3Team": "张岚，Rocio Perez Cano，李瑗瑗",
    "d3.closeProject": "关闭项目",

    // ProjectHci translations
    "hci.title": "人机交互设计",
    "hci.role": "课程项目",
    "hci.description":
      "在HCI课程和项目中，我学习了硬件和软件设计如何影响人与计算机之间的交互。我们还成功完成了HCI膳食限制辅助技术项目。",
    "hci.myRole": "UI/UX设计师",
    "hci.skillsUtilized":
      "用户访谈和分析，UI设计（Adobe PhotoShop），低保真原型和高保真原型（Axure RP/Sketch 3）",
    "hci.roleLabel": "我的角色：",
    "hci.skillsLabel": "运用技能：",
    "hci.problems": "问题",
    "hci.problemsDesc":
      "当第一次来到大学校园时，新生被迫在学习过程中快速适应新的社会和工作环境。这项任务本身就令人生畏，但当学生的饮食因胃肠道疾病、严重过敏或其他饮食限制而受到限制时，就变得更加困难。有这些疾病的学生在没有时间或精力确保维持饮食时，往往可能危及健康，当他们无法与朋友在同样的地方用餐时，也可能在社交上感到受限。",
    "hci.solutions": "我们的解决方案",
    "hci.solutionsDesc":
      "为了帮助有饮食限制的康奈尔学生，特别是新生，我们开发了Cornell MenU：一个移动应用程序，旨在根据用户的饮食限制提供校内餐厅的精简和筛选菜单信息，同时将新生与有类似情况的老生联系起来，创建信息共享网络。",
    "hci.paperPrototypes": "纸质原型",
    "hci.paperPrototypesDesc":
      "我们的产品是一个移动应用程序，显然满足了用户需要的第一个目标；一个用户友好且方便的应用格式。我们首先使用纸质原型来测试概念，并从目标学生那里获得快速反馈。",
    "hci.lowQualityPrototypes": "低保真原型：",
    "hci.lowQualityDemo": "低保真原型演示",
    "hci.highQualityPrototypes": "高保真原型",
    "hci.highQualityDesc":
      "在对纸质原型进行个人和小组启发式评估后，我们使用Photoshop和Axure RP构建了高保真原型。",
    "hci.highQualityPrototypes2": "高保真原型",
    "hci.iterativePrototypes": "迭代原型",
    "hci.highQualityPrototypes3": "高保真原型：",
    "hci.ourPoster": "我们的海报",
    "hci.ourTeam": "我们的团队",
    "hci.dateLabel": "时间：",
    "hci.teamMembersLabel": "团队成员：",
    "hci.dateRange": "2016年5月 - 2016年5月",
    "hci.teamMembers": "张岚，Rocio Perez Cano，李瑗瑗",
    "hci.closeProject": "关闭项目",

    // ProjectSe translations
    "se.title": "软件工程项目",
    "se.role": "课程项目",
    "se.description":
      "开发了一个网络应用程序，使研究人员能够在线进行调查和研究调查",
    "se.myRole": "前端开发工程师",
    "se.skillsUtilized":
      "需求分析，快速原型设计（Axure），前端开发（html，javascript，jade，node.js，bootstrap，mongoDB）",
    "se.roleLabel": "我的角色：",
    "se.skillsLabel": "运用技能：",
    "se.swDevProcess": "软件开发流程",
    "se.feasibility": "可行性研究和规划",
    "se.feasibilityDesc":
      "定义本项目的目标是为MERID项目开发视频托管和文件系统组件，该项目正在研究管弦乐队的创造力，并将该组件与MERID系统的其余部分集成。",
    "se.feasibilityDocs": "-文档：",
    "se.requirements": "需求分析",
    "se.requirementsDesc": "通过与客户协商确定系统的功能、约束和目标。",
    "se.requirementsNote":
      "使用迭代式快速原型进一步与客户沟通。获得了客户对原型设计的高度赞赏。",
    "se.documentsLabel": "文档：",
    "se.seeDemo": "查看演示",
    "se.systemDesign": "系统和程序设计",
    "se.implementation": "实现（编码）",
    "se.implementationDesc": "迭代开发并实现每个核心功能。",
    "se.launchApp": "启动应用",
    "se.systemDesignTitle": "系统设计",
    "se.dataModel": "数据模型",
    "se.useCases": "用例",
    "se.prototypes": "原型",
    "se.dateLabel": "时间：",
    "se.clientLabel": "客户：",
    "se.teamMembersLabel": "团队成员：",
    "se.dateRange": "2015年9月 - 2015年12月",
    "se.client": "Graeme Bailey，计算机科学系；Celine Brass，技术顾问和联络员",
    "se.teamMembers":
      "李瑗瑗（Maggie），Chenxi Su，Lucas Derraugh，Laurence Rosenzweig，Darcy A. Branchini，Chen Fang，Vinita Gogate",
    "se.closeProject": "关闭项目",

    // ProjectFitCat translations
    "fitcat.title": "iOS设计与开发",
    "fitcat.role": "康奈尔猫科健康项目",
    "fitcat.description":
      "为康奈尔猫科健康中心开发了一个移动应用程序，以改善猫咪的健康和福祉",
    "fitcat.myRole": "UI设计师和iOS开发工程师",
    "fitcat.skillsUtilized": "iOS平台编程，UI设计和UX设计",
    "fitcat.roleLabel": "我的角色：",
    "fitcat.skillsLabel": "运用技能：",
    "fitcat.requirements": "需求",
    "fitcat.requirementsDesc1":
      "康奈尔猫科健康中心致力于通过以下方式改善世界各地猫咪的健康和福祉：（1）通过提供及时的医疗信息和促进公众对猫科问题的理解和认识，教育兽医专业人员、猫主人、繁殖者和保护主义者；（2）通过进行和赞助突破性的猫科健康研究，寻找预防和治愈猫病的方法；（3）当出现新的或未知的猫病时为兽医提供帮助。",
    "fitcat.requirementsDesc2":
      "FitCat减肥是一个四步应用程序，包括以下步骤：（1）制定减肥计划，（2）帮助主人购买符合计划的食物，（3）记录猫咪吃的食物，（4）跟踪实际与计划的减肥情况。本项目的目标是仅实现规划步骤。规划步骤涉及：（1）教导主人如何判断猫咪的身体状况（对猫咪肥胖程度的视觉/触觉测量），（2）从主人那里获得实际身体状况，（3）使用猫咪的实际体重和推荐的安全减肥率来预测目标体重、达到目标的时间，以及猫咪每天的最大卡路里和脂肪克数，以在给定时间内达到目标。",
    "fitcat.prototypesV1": "原型版本1",
    "fitcat.prototypesDemo": "原型演示",
    "fitcat.prototypesV2": "原型版本2",
    "fitcat.prototypesV2Desc":
      "在迭代原型中，我们更改了UI设计，使其与康奈尔猫科中心品牌保持一致。",
    "fitcat.sketch3": "Sketch 3图片",
    "fitcat.iosDemo": "iOS演示-真实应用",
    "fitcat.presentationPoster": "演示海报",
    "fitcat.ourTeam": "我们的团队",
    "fitcat.dateLabel": "时间：",
    "fitcat.clientsLabel": "客户：",
    "fitcat.dateRange": "2016年1月 - 2016年5月",
    "fitcat.clients":
      "Dr. Bruce Kornreich，兽医学院；Paul Cashman，History in Motion创始人",
    "fitcat.closeProject": "关闭项目",

    // ProjectGiveNext translations
    "givenext.title": "商业咨询项目",
    "givenext.role": "客户：捐赠创业公司GiveNext",
    "givenext.description":
      "与MBA学生合作为创业公司GiveNext制定营销策略以吸引更多捐赠者，并设计了移动应用原型",
    "givenext.myRole": "顾问，UI/UX设计师",
    "givenext.skillsUtilized":
      "营销和品牌分析，市场研究和竞争分析，UI和UX设计，战略设计思维方法，营销和产品开发",
    "givenext.roleLabel": "我的角色：",
    "givenext.skillsLabel": "运用技能：",
    "givenext.aboutClient": "关于客户",
    "givenext.clientDesc":
      "GiveNext是捐赠领域的eTrade™。GiveNext是一个移动和在线平台，捐赠者可以与他们关心的事业合作，进行交易并管理他们的慈善捐赠。一旦慈善机构组合编入捐赠者账户，捐赠者的付款信息输入一次后，GiveNext就成为最简单的捐赠方式。捐赠者可以即时获得捐赠税收收据和慈善支持的移动历史记录。",
    "givenext.aboutClientDesc":
      "GiveNext是捐赠领域的eTrade™。GiveNext是一个移动和在线平台，捐赠者可以与他们关心的事业合作，进行交易并管理他们的慈善捐赠。一旦慈善机构组合编入捐赠者账户，捐赠者的付款信息输入一次后，GiveNext就成为最简单的捐赠方式。捐赠者可以即时获得捐赠税收收据和慈善支持的移动历史记录。",
    "givenext.giveNextHome": "GiveNext主页",
    "givenext.ourActivities": "我们的活动",
    "givenext.activity1":
      "研究慈善捐赠市场，了解捐赠者和非营利组织的行为和决策制定",
    "givenext.activity2": "对在线捐赠（支付处理）市场进行竞争分析",
    "givenext.activity3":
      "识别目标市场的关键产品优势，并推荐下一版本产品的关键产品功能和增强功能",
    "givenext.activity4":
      "制定营销计划和品牌战略，引导GiveNext进入慈善捐赠和非营利行业",
    "givenext.prototypesDemo": "原型演示",
    "givenext.ourTeam": "我们的团队",
    "givenext.dateLabel": "时间：",
    "givenext.clientLabel": "客户：",
    "givenext.teamMembersLabel": "团队成员：",
    "givenext.dateRange": "2016年1月 - 2016年5月",
    "givenext.client": "Daniel J Mansoor，GiveNext.com创始人兼首席执行官",
    "givenext.teamMembers":
      "Jess Roth，Han-Eol (Ryan) Jang，李瑗瑗（Maggie），Keyan (Kelly) Li，Tejaswini (TJ) Marathe",
    "givenext.closeProject": "关闭项目",

    // ProjectAiAssistant translations
    "aiassistant.title": "AI 个人助手 —— 支持语音的对话式聊天机器人",
    "aiassistant.role": "个人项目 · 独立设计与开发",
    "aiassistant.tagline":
      "大多数工程师会直接上向量数据库和 RAG。而我验证了整份履历只占用不到 1% 的上下文窗口——于是有意选择了更简单的方案。",
    "aiassistant.overview":
      "一个能回答招聘者问题的 AI——「她搭建过哪些数据平台？」「她的领导风格如何？」——严格基于我的真实履历，支持文字，以及用克隆自我本人音色的中英文语音。",
    "aiassistant.statCost": "$3.65",
    "aiassistant.statCostLabel": "每月运行成本",
    "aiassistant.statContext": "<1%",
    "aiassistant.statContextLabel": "上下文窗口占用",
    "aiassistant.statLines": "~900",
    "aiassistant.statLinesLabel": "行 RAG 代码被删除",
    "aiassistant.statVoice": "我的声音",
    "aiassistant.statVoiceLabel": "AI 克隆 · 中英双语",
    "aiassistant.roleLabel": "角色",
    "aiassistant.myRole": "独立设计与开发 —— 全栈、AI、基础设施",
    "aiassistant.stackLabel": "技术栈",
    "aiassistant.stack":
      "Python · FastAPI · Anthropic Claude · ElevenLabs · AWS Lambda + API Gateway (SAM)",
    "aiassistant.githubLabel": "代码",
    "aiassistant.tryItLive": "在线体验 → 本网站上的聊天小部件正是这套后端。",
    "aiassistant.decisionTitle": "关键决策：CAG，而非 RAG",
    "aiassistant.decisionIntro":
      "履历 = 约 37KB（约 1,500 个 token）。Claude 窗口 = 200,000 个 token。根本没有可供「检索」的对象——整个语料早已能完整放下。",
    "aiassistant.ragHead": "RAG —— 教科书式默认方案",
    "aiassistant.ragPoint1": "向量数据库、嵌入、分块、相似度调优",
    "aiassistant.ragPoint2": "为放不进上下文的海量语料而设计",
    "aiassistant.ragPoint3": "排错片段 → 答案出错，且难以调试",
    "aiassistant.ragVerdict": "这里属于过度设计",
    "aiassistant.cagHead": "CAG —— 我实际交付的方案",
    "aiassistant.cagPoint1": "每次请求都把完整履历放进系统提示",
    "aiassistant.cagPoint2":
      "Anthropic prompt caching → 只处理一次，而非每次调用",
    "aiassistant.cagPoint3": "Claude 看到 100% 上下文 → 没有检索失败",
    "aiassistant.cagVerdict": "更简单 · 更省钱 · 有据可依",
    "aiassistant.archTitle": "架构",
    "aiassistant.archApp":
      "FastAPI on Lambda —— API 端点、请求校验、CORS、成本护栏",
    "aiassistant.archAi":
      "Claude (Haiku 4.5) —— 完整履历置于经 prompt caching 的系统提示中",
    "aiassistant.archSession": "会话管理 —— 按会话维护的多轮对话历史",
    "aiassistant.archServices":
      "ElevenLabs —— 语音播放 + 实时语音对话，中英文自动识别",
    "aiassistant.featuresTitle": "功能特性",
    "aiassistant.featuresGrounded":
      "有据可依的问答 —— 回答严格限定在我的真实履历内，拒绝臆测。",
    "aiassistant.featuresMultiturn":
      "多轮对话 —— 按会话维护历史，使追问延续上下文。",
    "aiassistant.featuresVoice":
      "用我本人的声音说话 —— 通过 ElevenLabs 克隆我的音色与口音，用中英文朗读每个回答（自动识别）。",
    "aiassistant.featuresRealtime":
      "实时语音 —— 真正的语音一来一往，而不仅是播放。",
    "aiassistant.featuresCost": "成本控制 —— 请求硬上限、限流以及预算告警。",
    "aiassistant.highlightsTitle": "工程亮点",
    "aiassistant.highlightsRightsized":
      "恰到好处的架构 —— 选择 CAG 而非 RAG；不引入项目并不需要的数据库。",
    "aiassistant.highlightsCaching":
      "prompt caching —— 每次提示中最大的部分只处理一次，而非每次请求重复处理。",
    "aiassistant.highlightsDegradation":
      "优雅降级 —— 语音可选；没有任何单一依赖会拖垮整个服务。",
    "aiassistant.highlightsPrivacy":
      "隐私优先设计 —— 系统提示设定边界；对任何受众都安全。",
    "aiassistant.highlightsIac":
      "基础设施即代码 —— 整套无服务器栈定义在一个 SAM 模板中，一条命令部署。",
    "aiassistant.highlightsSecrets":
      "密钥规范 —— 密钥存于环境变量 / SAM 参数，绝不写进代码；uv、mypy、black、pytest。",
    "aiassistant.deployTitle": "部署与成本",
    "aiassistant.deployChip1": "无服务器 · AWS Lambda + API Gateway",
    "aiassistant.deployChip2": "每月约 $3.65",
    "aiassistant.deployChip3": "$5 预算告警",
    "aiassistant.deployChip4": "10 req/s 限流 · 90 万/月上限",
    "aiassistant.deployChip5": "冷启动 2–3 秒",
    "aiassistant.deployChip6": "一条命令 SAM 部署",
    "aiassistant.imageCaption": "聊天小部件 —— 本网站在线运行",
    "aiassistant.dateLabel": "时间",
    "aiassistant.date": "2026年3月",
    "aiassistant.closeProject": "关闭项目",

    // ProjectDag translations
    "dag.title": "DAG 工作流引擎 —— 可视化编排 + 并发执行器",
    "dag.role": "个人项目 · 独立设计与开发",
    "dag.tagline":
      "一个轻量版的 Airflow / Temporal：可视化地设计工作流，在运行前就获得 DAG 正确性保证，然后以真正的并发执行相互独立的分支。",
    "dag.overview":
      "手写脚本脆弱，Airflow、Temporal 又对许多任务杀鸡用牛刀。这个引擎瞄准中间地带——可视化编写、校验图、让独立步骤并行执行，并在出问题时进行交互式调试。",
    "dag.statFullstack": "全栈",
    "dag.statFullstackLabel": "可视化编排 + 异步执行引擎",
    "dag.statLight": "轻量",
    "dag.statLightLabel": "单进程，无重型基础设施",
    "dag.statExtensible": "易扩展",
    "dag.statExtensibleLabel": "节点类型经 JSON 配置",
    "dag.statResilient": "有韧性",
    "dag.statResilientLabel": "重试从失败处续跑",
    "dag.roleLabel": "角色",
    "dag.myRole": "独立设计与开发 —— 全栈、图算法、异步执行、基础设施",
    "dag.stackLabel": "技术栈",
    "dag.stack":
      "FastAPI · Python 3.13（async）· SQLAlchemy + Alembic · React + TypeScript · React Flow · TanStack Query · PostgreSQL · httpx · Docker Compose",
    "dag.scaleLabel": "规模",
    "dag.scale":
      "后端约 2.4K 行 Python，前端约 5.6K 行 TS/TSX，6 张 PostgreSQL 表——小到足以端到端理解，又大到足以体现真正的工程能力。",
    "dag.githubLabel": "代码",
    "dag.editorCaption": "可视化工作流编辑器（React Flow 画布）",
    "dag.archTitle": "架构",
    "dag.archFlowUi": "设计 · 监控 · 单步调试",
    "dag.archFlowApi": "校验 · 调度 · 执行",
    "dag.archFlowDb": "6 张表：workflows、nodes、edges、runs、node_runs、logs",
    "dag.archNodeIntro": "四种节点类型（配置存于灵活的 JSON 列中）：",
    "dag.archTwoPhase":
      "两阶段模型 —— 定义（静态图）与执行（每次运行的记录）相互分离。正是这种分离，使得从失败处重试、运行历史以及同一工作流的并发运行成为可能。",
    "dag.workflowCaption":
      "一个已构建的工作流 —— 并行 API 检查，并根据合并结果分支",
    "dag.deepDiveTitle": "技术深入",
    "dag.deepValidation":
      "DAG 校验 —— 保存/运行前的七项图检查，亮点是基于三色 DFS 的环检测。在设计期就发现环，远胜于在执行中途失败。",
    "dag.deepScheduler":
      "并发调度器 —— 就绪集 asyncio 循环用 asyncio.gather() 并发运行所有就绪节点，由信号量（10）限定，以免大范围扇出压垮下游 API 或数据库连接池。",
    "dag.deepBranching":
      "条件分支 —— 只有当节点所有活跃父边都成功时它才就绪；未被选中分支上的边会被排除，未触及的节点被清扫为 skipped。",
    "dag.deepRetry":
      "从失败处重试 —— 成功节点的输出被持久化；只有失败节点及其子树重置，因此不会因为九步里的第七步失败就把支付 API 再扣一次款。",
    "dag.deepStep":
      "单步调试 —— 执行器在每个节点后暂停并轮询信号；UI 用 /step 和 /continue 驱动，并优雅处理取消。",
    "dag.deepHttp":
      "有韧性的 HTTP 节点 —— httpx 配分类重试（重试超时/5xx，不重试 4xx）；沙箱模式将真实调用替换为模拟响应，使工作流在零外部依赖下可测试。",
    "dag.runCaption": "运行详情 —— 各节点状态、日志，以及活跃 vs. 跳过的路径",
    "dag.tradeoffsTitle": "设计决策与取舍",
    "dag.tradeoffExecutor":
      "单进程异步执行器 —— 易于调试；规模化时改用 Celery/RabbitMQ 工作进程做横向扩展，且无需改动图逻辑。",
    "dag.tradeoffPolling":
      "轮询（2 秒）而非 WebSocket —— 构建更快且足够好；WebSocket 推送是实现实时 UI 的迁移路径。",
    "dag.tradeoffTemplates":
      "{{var}} 模板字符串，而非任意表达式 —— 没有注入风险；只有在确需更丰富逻辑时才引入沙箱化求值器。",
    "dag.tradeoffScheduler":
      "就绪集循环，而非预先计算的拓扑排序 —— 这是必需的，因为分支会在运行期动态裁剪图。",
    "dag.testingTitle": "测试策略",
    "dag.testingDesc":
      "五个聚焦关键路径的测试，针对生产中真正会出问题的地方——并发、失败处理与恢复——而非行覆盖率：",
    "dag.testSequential": "顺序执行成功。",
    "dag.testParallel":
      "并行执行——3 个独立分支并发运行（断言这些 API 调用确实重叠）。",
    "dag.testFailure": "失败节点能正确中止执行。",
    "dag.testRetry": "从失败处重试会保留成功节点的输出。",
    "dag.testInactive": "非活跃的分支边不会阻塞下游节点。",
    "dag.dateLabel": "时间",
    "dag.date": "2026年3月",
    "dag.closeProject": "关闭项目",

    // ProjectRapid translations
    "rapid.title": "快速原型和物理计算",
    "rapid.role": "课程项目",
    "rapid.description":
      "学习现代快速原型技术，如激光切割、3D打印和微控制器编程（如Arduino系统）",
    "rapid.myRole": "程序员",
    "rapid.skillsUtilized":
      "微控制器（Photon），CAD程序（AutoDesk Inventor），使用激光切割机和3D打印机构建原型",
    "rapid.roleLabel": "我的角色：",
    "rapid.skillsLabel": "运用技能：",
    "rapid.design1": "设计1：交互设计",
    "rapid.design1Desc":
      "小型移动车是一个有趣游戏的设计。在游戏中，玩家可以通过电位器设置目标距离。然后玩家可以使用按钮控制迷你车到达目标距离。使用按钮有两种不同方式。一种是快速短按，另一种是长按。快速短按可以切换迷你车的运行或停止。长按可以切换车的方向（向前或向后移动）。当迷你车远离目标距离时，三色LED灯会变绿。当车在目标位置时，蓝灯会亮起。但如果车太接近目标物体，红灯会亮起。",
    "rapid.interactiveDemo": "交互设计：演示",
    "rapid.design2": "设计2：交互设计",
    "rapid.design2Desc":
      '我的第二个设计项目是一个由2D激光切割机制作的盒子。该盒子可以通过正确的输入代码触发打开。默认代码是"长按、短按、长按"。它还有一个扬声器来告诉你输入代码是否正确。当系统开始工作时，扬声器会播放音乐。然后你可以开始输入开启代码。如果你点击错误的动作，扬声器会播放错误声音。如果你正确点击代码，扬声器会播放音乐，盒子会打开。几秒钟后，盒子会自动关闭。如果你连接它，输入代码的状态也可以在控制台中看到。',
    "rapid.2dPrinterDemo": "2D打印机设计：演示",
    "rapid.design3": "设计3：3D拼图",
    "rapid.design3Desc1":
      "在第三个设计作业中，我制作了一个3D拼图Phillipe Dubois' Burr。这个拼图有六个不同的部分可以组装在一起。设计理念来自一个",
    "rapid.design3Desc2":
      "在这个设计中，我使用Inventory绘制可以在3D打印机中打印的2D草图和3D对象",
    "rapid.3dPrinterDemo": "3D打印机设计：演示",
    "rapid.finalProject": "最终项目：动态艺术（团队作业）",
    "rapid.finalProjectDesc1":
      "动态艺术是一种基于所包含的运动向观众传达感情的艺术形式。我们将在项目中构建一个带有动态粒子的雕塑。原始想法类似于新加坡机场的动态雨。",
    "rapid.finalProjectDesc2":
      "我们的最终设计有3排悬挂粒子，可以移动到几种不同的已编程模式。这些模式可以跟随正弦波、余弦波和其他形状。我们使用步进电机控制粒子的位置。我们还有9个可以改变颜色的三色LED灯。这些LED灯可以帮助丙烯酸制成的粒子看起来美丽。",
    "rapid.presentationPoster": "演示海报",
    "rapid.kineticDemo": "动态艺术：演示",
    "rapid.dateLabel": "时间：",
    "rapid.date": "2016年5月",
    "rapid.closeProject": "关闭项目",

    // ProjectCscw translations
    "cscw.title": "计算机支持协作研究",
    "cscw.role": "促进协作研究实验室",
    "cscw.description":
      "为协作团队设计高效和有效的流程。在这次研究经历中，我提高了研究和写作技能",
    "cscw.myRole": "用户体验研究员",
    "cscw.skillsUtilized":
      "用户体验研究，数据分析（SPSS），php网页开发和写作技能",
    "cscw.roleLabel": "我的角色：",
    "cscw.skillsLabel": "运用技能：",
    "cscw.researchExperience": "研究经历",
    "cscw.experience1": "通过回顾信息系统顶级期刊的30多篇论文构建协作模型",
    "cscw.experience2": "独立确定研究主题",
    "cscw.experience3": "在实验后采访了100多人",
    "cscw.experience4": "分析了100多份转录记录并自学统计分析方法",
    "cscw.experience5": "自主开发工具：",
    "cscw.publications": "发表论文",
    "cscw.publication1":
      "《一种新颖协作工程方法在学习设计中的应用：案例研究》，SSCI期刊：英国教育技术杂志，2015年",
    "cscw.publication2":
      "《我们能解决低参与度、分心和低效率问题吗？工业分布式协作学习案例研究》，第48届夏威夷国际系统科学会议（信息系统领域第2名），2015年",
    "cscw.publication3":
      "《酒店业协作信息系统采用》，第18届太平洋亚洲信息系统会议，2014年",
    "cscw.publication4":
      "《学生协作小组工作的简易协作过程支持系统设计》，第47届夏威夷国际系统科学会议，2014年",
    "cscw.publication5":
      "《学生组织的在线协作过程设计》，理论与应用信息技术杂志，2012年",
    "cscw.designBrainstorming": "设计头脑风暴",
    "cscw.firstSketch": "初步草图",
    "cscw.finalDesign": "最终设计",
    "cscw.responsiveDev": "响应式开发",
    "cscw.responsiveDevelopment": "响应式开发",
    "cscw.techStack": "PHP, Apache, Windows, MySQL",
    "cscw.launchedApp": "发布应用",
    "cscw.seeDemo": "查看演示",
    "cscw.seeDetails": "查看详情",
    "cscw.viewPdf": "查看PDF",
    "cscw.dateLabel": "时间：",
    "cscw.dateRange": "2012年10月 - 2014年10月",
    "cscw.closeProject": "关闭项目",

    // ProjectDataProduct translations
    "dataproduct.title": "与MBA学生一起设计数据产品",
    "dataproduct.role": "课程项目",
    "dataproduct.description":
      "学习使用回归、KNN聚类、朴素贝叶斯和决策树来解决业务问题，并提出数据产品设计",
    "dataproduct.myRole": "开发工程师，数据科学家",
    "dataproduct.skillsUtilized": "数据分析和可视化，演示",
    "dataproduct.roleLabel": "我的角色：",
    "dataproduct.skillsLabel": "运用技能：",
    "dataproduct.seeDemo": "查看演示",
    "dataproduct.dateLabel": "时间：",
    "dataproduct.teamMembersLabel": "团队成员：",
    "dataproduct.closeProject": "关闭项目",
    "dataproduct.dateRange": "2016年3月 - 2016年4月",
    "dataproduct.teamMembers":
      "Jack Zoephel, Tyler Done, Sowmya Ballakur, Ryan Armstrong, Yuanyuan Li",

    // ProjectNonprofit translations
    "nonprofit.title": "非营利组织商业解决方案",
    "nonprofit.role": "黑客马拉松项目",
    "nonprofit.description": "设计了一个解决方案来帮助提高对食物可及性的认识",
    "nonprofit.myRole": "开发工程师",
    "nonprofit.skillsUtilized": "敏捷网络开发，业务分析和演示",
    "nonprofit.roleLabel": "我的角色：",
    "nonprofit.skillsLabel": "运用技能：",
    "nonprofit.targetProblems": "目标问题",
    "nonprofit.ourSolutions": "我们的解决方案",
    "nonprofit.problemDesc": "伊萨卡家庭和儿童服务以及食品不安全问题",
    "nonprofit.problemDescription":
      "伊萨卡家庭和儿童服务机构目睹了低收入个人和家庭以及贫困人口缺乏基本资源来每天养活自己或家人的情况。许多人坐着或站着举着标语牌要求提供食物、住房或基本生存的资金。社区成员有时会用金钱和/或食物回应。举牌的行为可能会降低个人的自尊心，同时也不受社区成员欢迎。该组织希望看到个人捐赠和接收他们吃饭所需资金的替代方式。",
    "nonprofit.solutionDesc":
      "Pro Bonobos是一个旨在帮助提高对食品不安全认识的项目。它将帮助低收入个人和贫困家庭每天养活自己",
    "nonprofit.solutionDescription":
      "Pro Bonobos是一个旨在帮助提高对食品不安全认识的项目。它将帮助低收入个人和贫困家庭每天养活自己",
    "nonprofit.seeDemo": "查看演示",
    "nonprofit.dateLabel": "时间：",
    "nonprofit.clientLabel": "客户：",
    "nonprofit.teamMembersLabel": "团队成员：",
    "nonprofit.closeProject": "关闭项目",
    "nonprofit.date": "2015年11月",
    "nonprofit.client": "Tammy Baker，伊萨卡家庭和儿童服务机构",
    "nonprofit.teamMembers":
      "Karen Tan, Jiyoung Oh, Yuanyuan Li, Jennifer Lee, Greta Jenkins, Brett Granum",

    // Chat Widget
    "widget.title": "李瑗瑗",
    "widget.subtitle": "专业简介",
    "widget.placeholder": "询问瑗瑗的背景...",
    "widget.welcomeMessage":
      "欢迎！我在这里帮助您了解李瑗瑗的专业背景、经验和专业知识。您可以询问关于她的工作经历、项目、技能或领导风格的任何问题。",
    "widget.newChat": "新对话",
    "widget.toggleSidebar": "切换侧边栏",
    "widget.toggleTheme": "切换主题",
    "widget.close": "关闭",
    "widget.send": "发送消息",
    "widget.loading": "加载中...",
    "widget.openChat": "打开聊天",
    "widget.suggestedHeader": "试着问：",
    "widget.suggested.about": "介绍一下Yuanyuan",
    "widget.suggested.dataSystems": "她构建了哪些数据系统？",
    "widget.suggested.aws": "她的AWS经验如何？",
    "widget.suggested.leadership": "描述一下她的领导风格",
    "widget.suggested.projects": "她在Two Sigma做了哪些项目？",
    "widget.sources": "来源",
    "widget.error": "抱歉，出了点问题。请重试。",

    // Voice playback
    "widget.voice.play": "▶ 朗读",
    "widget.voice.loading": "生成音频中...",
    "widget.voice.pause": "⏸ 暂停",
    "widget.voice.resume": "▶ 继续",
    "widget.voice.replay": "↻ 重播",
    "widget.voice.error": "音频不可用",
    "widget.voice.tooLong": "此回复太长，无法播放音频。",
    "widget.voice.tooMany": "音频请求过多，请稍后再试。",
    "widget.voice.quotaExceeded": "本月音频配额已用完。",
    "widget.voice.disclosure": "语音由AI生成。",
    "widget.voice.autoPlay": "自动播放音频",
    "widget.voice.aria.play": "朗读此回复",
    "widget.voice.aria.pause": "暂停音频播放",
    "widget.voice.aria.resume": "继续音频播放",
    "widget.voice.aria.replay": "重播音频",
    "widget.voice.aria.toggle": "切换自动播放音频",

    // Voice conversation
    "voice.call.start": "与袁媛的 AI 对话",
    "voice.call.end": "结束通话",
    "voice.call.retry": "重试",
    "voice.call.close": "关闭",
    "voice.call.connecting": "连接中...",
    "voice.call.ai_speaking": "AI 正在说话",
    "voice.call.listening": "正在聆听...",
    "voice.call.transcript_label": "实时字幕:",
    "voice.call.ended": "通话结束",
    "voice.call.in_call": "通话中",
    "voice.call.error.mic_denied": "麦克风权限被拒绝，请允许后重试。",
    "voice.call.error.mic_unavailable": "麦克风不可用。",
    "voice.call.error.session_failed": "无法启动语音会话，请重试。",
    "voice.call.error.connection_lost": "语音连接中断。",
    "voice.call.aria.start": "开始语音对话",
    "voice.call.aria.end": "结束语音对话",
  },
};

// Detect user's preferred language
const detectUserLanguage = (): Language => {
  // Check localStorage first
  const savedLanguage = localStorage.getItem("preferred-language") as Language;
  if (savedLanguage && (savedLanguage === "en" || savedLanguage === "zh")) {
    return savedLanguage;
  }

  // Check browser language
  const browserLanguage = navigator.language || navigator.languages[0];
  if (browserLanguage.startsWith("zh")) {
    return "zh";
  }

  // Default to English
  return "en";
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>(detectUserLanguage);

  useEffect(() => {
    // Save language preference to localStorage when it changes
    localStorage.setItem("preferred-language", language);
    // Keep <html lang> in sync so screen readers and search engines
    // interpret the page content in the correct language.
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return (
      translations[language][
        key as keyof (typeof translations)[typeof language]
      ] || key
    );
  };

  const value = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
